/**
 * ThemeTonic Theme - Core JavaScript Module
 *
 * Performance targets:
 * - Total size: < 8KB minified
 * - No jQuery dependencies
 * - Event delegation for minimal listeners
 * - IntersectionObserver for scroll-based features
 *
 * @version 1.0.0
 */

/* ==========================================================================
   DEBUG MODE
   ========================================================================== */

// Enable debug mode only in Shopify preview/design mode or if explicitly enabled
// Security: Restricted to prevent information disclosure in production
const DEBUG = window.Shopify?.designMode ||
              (window.location.search.includes('debug=true') && window.Shopify?.designMode);

// Debug-safe console methods
const logger = {
  log: (...args) => DEBUG && console.log('[ThemeTonic]', ...args),
  warn: (...args) => DEBUG && console.warn('[ThemeTonic]', ...args),
  error: (...args) => console.error('[ThemeTonic]', ...args), // Always log errors
  info: (...args) => DEBUG && console.info('[ThemeTonic]', ...args)
};

/* ==========================================================================
   GLOBAL UTILITIES
   ========================================================================== */

/**
 * Shopify money formatter
 * Uses shop's money format from Liquid
 * Security: Validates input to prevent NaN display
 */
window.formatMoney = function(cents, format) {
  // Handle null, undefined, or invalid input
  if (cents === null || cents === undefined) {
    cents = 0;
  }
  if (typeof cents === 'string') {
    cents = cents.replace(/[^0-9.-]/g, ''); // Remove non-numeric except decimal/minus
  }
  const numericCents = parseFloat(cents);
  // Validate conversion - default to 0 if NaN
  const value = isNaN(numericCents) ? 0 : numericCents / 100;
  const formatString = format || window.Shopify?.money_format || '${{amount}}';

  const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
    if (isNaN(number) || number == null) return 0;
    const parts = number.toFixed(precision).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return parts.join(decimal);
  };

  let formattedValue = '';
  switch (formatString.match(/\{\{\s*(\w+)\s*\}\}/)?.[1]) {
    case 'amount':
      formattedValue = formatWithDelimiters(value, 2);
      break;
    case 'amount_no_decimals':
      formattedValue = formatWithDelimiters(value, 0);
      break;
    case 'amount_with_comma_separator':
      formattedValue = formatWithDelimiters(value, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      formattedValue = formatWithDelimiters(value, 0, '.', ',');
      break;
    default:
      formattedValue = formatWithDelimiters(value, 2);
  }

  return formatString.replace(/\{\{\s*\w+\s*\}\}/, formattedValue);
};

/**
 * Debounce function for rate-limiting
 */
window.debounce = function(fn, wait) {
  let timeout;
  return function(...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn.apply(this, args), wait);
  };
};

/**
 * Header Height Calculator (Enterprise pattern)
 * Calculates combined announcement bar + header height
 * Sets CSS variables for sticky positioning coordination
 */
window.setHeaderHeight = function() {
  let totalHeight = 0;

  // Get announcement bar height from the bar element itself (not the section wrapper,
  // because x-show hides the bar but the wrapper may keep its height)
  const announcementEl = document.querySelector('.announcement-bar');
  const announcementHeight = (announcementEl && announcementEl.offsetHeight > 0) ? announcementEl.offsetHeight : 0;
  totalHeight += announcementHeight;

  // Get utility bar height (sits between announcement bar and header)
  const utilityEl = document.querySelector('.utility-bar');
  const utilityHeight = (utilityEl && utilityEl.offsetHeight > 0 && !utilityEl.classList.contains('utility-bar--collapsed')) ? utilityEl.offsetHeight : 0;
  totalHeight += utilityHeight;

  // Combined height of bars above the header (for sticky top offset)
  const barsHeight = announcementHeight + utilityHeight;

  // Get header height via its element class
  const headerEl = document.querySelector('.header');
  const headerHeight = headerEl ? headerEl.offsetHeight : 70;
  totalHeight += headerHeight;

  // Set CSS variables for positioning coordination
  document.documentElement.style.setProperty('--announcement-bar-height', announcementHeight + 'px');
  document.documentElement.style.setProperty('--utility-bar-height', utilityHeight + 'px');
  document.documentElement.style.setProperty('--bars-height', barsHeight + 'px');
  document.documentElement.style.setProperty('--header-height', headerHeight + 'px');
  document.documentElement.style.setProperty('--total-header-height', totalHeight + 'px');

  logger.log('Header heights updated:', { announcementHeight, utilityHeight, barsHeight, headerHeight, totalHeight });
};

// Initialize header height on DOM ready, after full load, and on resize
document.addEventListener('DOMContentLoaded', window.setHeaderHeight);
window.addEventListener('load', window.setHeaderHeight);
window.addEventListener('resize', window.debounce(window.setHeaderHeight, 100));

// Recalculate when bars hide/show on scroll (utility bar collapse, announcement dismiss)
// Uses ResizeObserver for efficient detection instead of polling on every scroll frame
if (typeof ResizeObserver !== 'undefined') {
  const barObserver = new ResizeObserver(window.debounce(window.setHeaderHeight, 50));
  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('.announcement-bar, .utility-bar').forEach(el => barObserver.observe(el));
  });
}

/**
 * Fetch with error handling
 */
window.fetchJSON = async function(url, options = {}) {
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...options.headers
    }
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
};

/**
 * Fetch with retry logic and exponential backoff
 * Handles transient failures (429, 503) automatically
 * @param {string} url - Request URL
 * @param {object} options - Fetch options
 * @param {object} retryConfig - Retry configuration
 * @returns {Promise<Response>} Fetch response
 */
window.fetchWithRetry = async function(url, options = {}, retryConfig = {}) {
  const {
    maxRetries = 3,
    baseDelay = 500,
    maxDelay = 5000,
    retryableStatuses = [429, 503, 502, 504],
    signal = null
  } = retryConfig;

  let lastError;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      // Check if request was aborted
      if (signal?.aborted) {
        throw new DOMException('Request aborted', 'AbortError');
      }

      const response = await fetch(url, {
        ...options,
        signal,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...options.headers
        }
      });

      // Check for retryable status codes
      if (!response.ok && retryableStatuses.includes(response.status)) {
        // Check for Retry-After header (for 429 responses)
        const retryAfter = response.headers.get('Retry-After');
        let delay;

        if (retryAfter) {
          // Retry-After can be seconds or HTTP-date
          delay = parseInt(retryAfter, 10) * 1000 || baseDelay;
        } else {
          // Exponential backoff: baseDelay * 2^attempt with jitter
          delay = Math.min(
            baseDelay * Math.pow(2, attempt) + Math.random() * 100,
            maxDelay
          );
        }

        if (attempt < maxRetries) {
          logger.warn(`Request failed with ${response.status}, retrying in ${delay}ms (attempt ${attempt + 1}/${maxRetries})`);
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        }
      }

      return response;
    } catch (error) {
      lastError = error;

      // Don't retry on abort
      if (error.name === 'AbortError') {
        throw error;
      }

      // Retry on network errors
      if (attempt < maxRetries) {
        const delay = Math.min(
          baseDelay * Math.pow(2, attempt) + Math.random() * 100,
          maxDelay
        );
        logger.warn(`Network error, retrying in ${delay}ms (attempt ${attempt + 1}/${maxRetries}):`, error.message);
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }
    }
  }

  throw lastError || new Error('Request failed after retries');
};

/**
 * Fetch JSON with retry logic
 * Combines fetchWithRetry with JSON parsing
 */
window.fetchJSONWithRetry = async function(url, options = {}, retryConfig = {}) {
  const response = await fetchWithRetry(url, options, retryConfig);
  if (!response.ok) {
    // Read Shopify error response for meaningful message
    let message = `HTTP ${response.status}`;
    try {
      const errorData = await response.json();
      if (errorData.description) message = errorData.description;
      else if (errorData.message) message = errorData.message;
      else if (errorData.errors) message = typeof errorData.errors === 'string' ? errorData.errors : JSON.stringify(errorData.errors);
    } catch (_) { /* response wasn't JSON */ }
    const error = new Error(message);
    error.status = response.status;
    throw error;
  }
  return response.json();
};

/**
 * HTML Sanitizer for preventing XSS attacks
 * Security: Removes dangerous elements and attributes from HTML strings
 * Used when updating DOM with server-rendered content
 */
window.sanitizeHTML = function(html) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  // Dangerous elements to remove completely (includes svg/math which can contain XSS)
  // Note: form/input are allowed - we sanitize their dangerous attributes instead
  const dangerousTags = ['script', 'iframe', 'object', 'embed', 'link', 'meta', 'base', 'svg', 'math', 'style'];
  dangerousTags.forEach(tag => {
    doc.querySelectorAll(tag).forEach(el => el.remove());
  });

  // Dangerous attributes to remove (includes style for CSS injection prevention)
  // Comprehensive list including HTML5 event handlers
  const dangerousAttrs = [
    // Mouse events
    'onclick', 'ondblclick', 'onmousedown', 'onmouseup', 'onmousemove',
    'onmouseover', 'onmouseout', 'onmouseenter', 'onmouseleave', 'onwheel',
    // Keyboard events
    'onkeydown', 'onkeyup', 'onkeypress',
    // Form events
    'onfocus', 'onblur', 'oninput', 'onchange', 'onsubmit', 'onreset', 'oninvalid',
    // Window/Document events
    'onload', 'onunload', 'onbeforeunload', 'onerror', 'onresize', 'onscroll',
    'onhashchange', 'onpopstate', 'onstorage', 'onpageshow', 'onpagehide',
    // Drag events
    'ondrag', 'ondragstart', 'ondragend', 'ondragenter', 'ondragleave', 'ondragover', 'ondrop',
    // Clipboard events
    'oncopy', 'oncut', 'onpaste',
    // Media events
    'onplay', 'onpause', 'onended', 'oncanplay', 'oncanplaythrough', 'onseeking', 'onseeked',
    'ontimeupdate', 'onvolumechange', 'onloadstart', 'onprogress', 'onloadeddata', 'onloadedmetadata',
    // Touch events
    'ontouchstart', 'ontouchend', 'ontouchmove', 'ontouchcancel',
    // Animation/Transition events
    'onanimationstart', 'onanimationend', 'onanimationiteration',
    'ontransitionend', 'ontransitionstart', 'ontransitioncancel', 'ontransitionrun',
    // Other dangerous events
    'oncontextmenu', 'onshow', 'ontoggle', 'onpointerdown', 'onpointerup',
    'onpointermove', 'onpointerenter', 'onpointerleave', 'onpointerover', 'onpointerout',
    // Dangerous attributes (not events)
    'style', 'formaction', 'autofocus', 'xlink:href', 'srcdoc'
  ];

  doc.querySelectorAll('*').forEach(el => {
    // Remove event handler attributes
    dangerousAttrs.forEach(attr => el.removeAttribute(attr));

    // Remove javascript: and vbscript: URLs from dangerous attributes
    ['href', 'src', 'srcset', 'srcdoc', 'action', 'data', 'poster'].forEach(attr => {
      const value = el.getAttribute(attr);
      if (value) {
        const trimmed = value.toLowerCase().trim();
        if (trimmed.startsWith('javascript:') || trimmed.startsWith('vbscript:')) {
          el.removeAttribute(attr);
        }
      }
    });

    // Remove data: URLs except for safe base64-encoded image types
    // Block svg data URLs as they can contain XSS
    const src = el.getAttribute('src');
    if (src) {
      const trimmed = src.toLowerCase().trim();
      if (trimmed.startsWith('data:')) {
        // Only allow safe raster image formats with proper base64 encoding
        // Explicitly block svg, html, xml, javascript
        const safeImageTypes = /^data:image\/(png|jpeg|jpg|gif|webp);base64,[A-Za-z0-9+/=]+$/;
        if (!safeImageTypes.test(trimmed)) {
          el.removeAttribute('src');
        }
      }
    }

    // Prevent DOM clobbering attacks
    const elId = el.getAttribute('id');
    const elName = el.getAttribute('name');
    const clobberingNames = ['length', 'name', 'item', 'namedItem', 'constructor', '__proto__'];
    if (elId && clobberingNames.includes(elId)) {
      el.removeAttribute('id');
    }
    if (elName && clobberingNames.includes(elName)) {
      el.removeAttribute('name');
    }
  });

  return doc.body.innerHTML;
};

/**
 * Validate JSON structure to prevent prototype pollution
 * Security: Checks for dangerous __proto__ and constructor properties
 */
window.validateJSON = function(data, requiredKeys = []) {
  if (!data || typeof data !== 'object') return false;
  if (data.__proto__ !== undefined && data.__proto__ !== Object.prototype) return false;
  if (data.constructor !== undefined && data.constructor !== Object) return false;
  if (data.prototype !== undefined) return false;

  // Check required keys exist
  for (const key of requiredKeys) {
    if (!(key in data)) return false;
  }

  return true;
};

/* ==========================================================================
   CART API
   ========================================================================== */

window.CartAPI = {
  routes: {
    get: () => `${window.Shopify?.routes?.root || '/'}cart.js`,
    add: () => `${window.Shopify?.routes?.root || '/'}cart/add.js`,
    update: () => `${window.Shopify?.routes?.root || '/'}cart/update.js`,
    change: () => `${window.Shopify?.routes?.root || '/'}cart/change.js`,
    clear: () => `${window.Shopify?.routes?.root || '/'}cart/clear.js`
  },

  // Pending request controllers for deduplication
  _pendingRequests: new Map(),

  async get(options = {}) {
    try {
      return await fetchJSONWithRetry(this.routes.get(), {}, { signal: options.signal });
    } catch (error) {
      if (error.name === 'AbortError') throw error;
      logger.error('CartAPI.get failed:', error);
      this.dispatchEvent('cart:error', { message: 'Failed to load cart', error });
      throw error;
    }
  },

  async add(items, sections = [], options = {}) {
    // Security: Validate items array structure
    if (!Array.isArray(items) || items.length === 0) {
      const error = new Error('Items must be a non-empty array');
      logger.error('CartAPI.add validation failed:', error);
      this.dispatchEvent('cart:error', { message: 'Invalid cart items', error });
      throw error;
    }

    // Validate each item structure
    for (const item of items) {
      if (!item.id || (typeof item.id !== 'number' && typeof item.id !== 'string')) {
        const error = new Error('Each item must have a valid id');
        logger.error('CartAPI.add validation failed:', error);
        throw error;
      }
      const qty = parseInt(item.quantity, 10);
      if (isNaN(qty) || qty < 1 || qty > 10000) {
        const error = new Error('Each item must have a valid quantity (1-10000)');
        logger.error('CartAPI.add validation failed:', error);
        throw error;
      }
      // Sanitize quantity to integer
      item.quantity = qty;
    }

    const body = { items };
    if (sections.length) body.sections = sections;

    try {
      const result = await fetchJSONWithRetry(this.routes.add(), {
        method: 'POST',
        body: JSON.stringify(body)
      }, { signal: options.signal });
      this.dispatchEvent('cart:add', result);
      return result;
    } catch (error) {
      if (error.name === 'AbortError') throw error;
      logger.error('CartAPI.add failed:', error);
      this.dispatchEvent('cart:error', { message: 'Failed to add item to cart', error });
      throw error;
    }
  },

  async update(updates, sections = [], options = {}) {
    const body = { updates };
    if (sections.length) body.sections = sections;

    try {
      const result = await fetchJSONWithRetry(this.routes.update(), {
        method: 'POST',
        body: JSON.stringify(body)
      }, { signal: options.signal });
      this.dispatchEvent('cart:update', result);
      return result;
    } catch (error) {
      if (error.name === 'AbortError') throw error;
      logger.error('CartAPI.update failed:', error);
      this.dispatchEvent('cart:error', { message: 'Failed to update cart', error });
      throw error;
    }
  },

  async change(lineKey, quantity, sections = [], options = {}) {
    // Security: Validate line key parameter
    if (!lineKey || typeof lineKey !== 'string') {
      const error = new Error('Line key must be a valid string');
      logger.error('CartAPI.change validation failed:', error);
      throw error;
    }

    // Validate quantity
    const qty = parseInt(quantity, 10);
    if (isNaN(qty) || qty < 0 || qty > 10000) {
      const error = new Error('Quantity must be a valid number (0-10000)');
      logger.error('CartAPI.change validation failed:', error);
      throw error;
    }

    // Cancel any pending request for the same line item
    const requestKey = `change:${lineKey}`;
    if (this._pendingRequests.has(requestKey)) {
      this._pendingRequests.get(requestKey).abort();
    }
    const controller = new AbortController();
    this._pendingRequests.set(requestKey, controller);

    // Use cart/change.js with line item key (Shopify-recommended for updating existing items)
    const body = { id: lineKey, quantity: qty };
    if (sections.length) body.sections = sections;

    try {
      const result = await fetchJSONWithRetry(this.routes.change(), {
        method: 'POST',
        body: JSON.stringify(body)
      }, { signal: options.signal || controller.signal });
      this._pendingRequests.delete(requestKey);
      this.dispatchEvent('cart:change', result);
      return result;
    } catch (error) {
      this._pendingRequests.delete(requestKey);
      if (error.name === 'AbortError') throw error;
      logger.error('CartAPI.change failed:', error);
      this.dispatchEvent('cart:error', { message: 'Failed to update quantity', error });
      throw error;
    }
  },

  async clear(options = {}) {
    try {
      const result = await fetchJSONWithRetry(this.routes.clear(), {
        method: 'POST'
      }, { signal: options.signal });
      this.dispatchEvent('cart:clear', result);
      return result;
    } catch (error) {
      if (error.name === 'AbortError') throw error;
      logger.error('CartAPI.clear failed:', error);
      this.dispatchEvent('cart:error', { message: 'Failed to clear cart', error });
      throw error;
    }
  },

  dispatchEvent(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail, bubbles: true }));
  }
};

/* ==========================================================================
   ALPINE.JS STORES (Global State Management)
   Pattern inspired by ThemeCraft/Slayed themes
   ========================================================================== */

document.addEventListener('alpine:init', () => {
  // Error boundary for Alpine initialization
  if (!window.Alpine) {
    logger.error('Alpine.js failed to load');
    document.body.classList.add('alpine-error');
    return;
  }

  /**
   * Delivery Timeline — live countdown + shipping dates
   */
  Alpine.data('deliveryTimeline', function(cfg) { return {
    countdown: '', shipDate: '', deliveryRange: '', _t: null,
    init: function() { var self = this; this._run(); this._t = setInterval(function() { self._run(); }, 1000); },
    destroy: function() { if (this._t) clearInterval(this._t); },
    _bd: function(d, n) {
      var r = new Date(d), a = 0;
      while (a < n) { r.setDate(r.getDate()+1); if (cfg.excludeWeekends && (r.getDay()===0||r.getDay()===6)) continue; a++; }
      return r;
    },
    _run: function() {
      var now = new Date(), h = now.getHours(), mi = now.getMinutes(), se = now.getSeconds();
      var loc = cfg.locale === 'it' ? 'it-IT' : 'en-GB', fmt = { day: '2-digit', month: '2-digit' }, co = cfg.cutoffHour;
      if (h < co) {
        var tot = (co*3600)-(h*3600+mi*60+se), hh=Math.floor(tot/3600), mm=Math.floor((tot%3600)/60), ss=tot%60;
        var p=[]; if(hh>0) p.push(hh+'H'); p.push(String(mm).padStart(2,'0')+'M'); p.push(String(ss).padStart(2,'0')+'S');
        this.countdown=p.join(' ');
      } else {
        var tmr=new Date(now); tmr.setDate(tmr.getDate()+1);
        if(cfg.excludeWeekends){while(tmr.getDay()===0||tmr.getDay()===6) tmr.setDate(tmr.getDate()+1);}
        tmr.setHours(co,0,0,0);
        var diff=Math.max(0,Math.floor((tmr-now)/1000)), hh=Math.floor(diff/3600), mm=Math.floor((diff%3600)/60), ss=diff%60;
        var p=[]; if(hh>0) p.push(hh+'H'); p.push(String(mm).padStart(2,'0')+'M'); p.push(String(ss).padStart(2,'0')+'S');
        this.countdown=p.join(' ');
      }
      var pd=cfg.processingDays; if(h>=co) pd++;
      var ship=this._bd(new Date(now),pd); this.shipDate=ship.toLocaleDateString(loc,fmt);
      var minD=this._bd(new Date(ship),cfg.shippingDaysMin), maxD=this._bd(new Date(ship),cfg.shippingDaysMax);
      var a=minD.toLocaleDateString(loc,fmt), b=maxD.toLocaleDateString(loc,fmt);
      this.deliveryRange = a===b ? a : a+' - '+b;
    }
  }; });

  /**
   * Cart Store - Centralized cart state
   * Usage in templates: $store.cart.count, $store.cart.total
   */
  Alpine.store('cart', {
    items: [],
    count: 0,
    total: 0,
    isLoading: false,
    freeShippingThreshold: 0,
    freeShippingProgress: 0,

    async init() {
      // Load initial cart state
      try {
        const cart = await CartAPI.get();
        this.updateFromCart(cart);
      } catch (error) {
        logger.error('Failed to initialize cart store:', error);
      }

      // Listen for cart events
      document.addEventListener('cart:add', (e) => this.updateFromCart(e.detail));
      document.addEventListener('cart:update', (e) => this.updateFromCart(e.detail));
      document.addEventListener('cart:change', (e) => this.updateFromCart(e.detail));
      document.addEventListener('cart:clear', () => this.clear());
    },

    updateFromCart(cart) {
      // Validate cart response structure
      if (!cart || typeof cart !== 'object') {
        logger.warn('Invalid cart response: expected object');
        return;
      }

      // Validate required fields exist and have expected types
      if (!Array.isArray(cart.items)) {
        logger.warn('Invalid cart response: items must be an array');
        this.items = [];
      } else {
        this.items = cart.items;
      }

      // Validate item_count is a number
      if (typeof cart.item_count !== 'number' || isNaN(cart.item_count)) {
        logger.warn('Invalid cart response: item_count must be a number');
        this.count = this.items.reduce((sum, item) => sum + (item.quantity || 0), 0);
      } else {
        this.count = cart.item_count;
      }

      // Validate total_price is a number
      if (typeof cart.total_price !== 'number' || isNaN(cart.total_price)) {
        logger.warn('Invalid cart response: total_price must be a number');
        this.total = this.items.reduce((sum, item) => sum + (item.line_price || 0), 0);
      } else {
        this.total = cart.total_price;
      }

      this.updateFreeShippingProgress();
    },

    updateFreeShippingProgress() {
      if (!this.freeShippingThreshold || this.freeShippingThreshold <= 0) {
        this.freeShippingProgress = 0;
        return;
      }
      this.freeShippingProgress = Math.min(100, (this.total / this.freeShippingThreshold) * 100);
    },

    setFreeShippingThreshold(threshold) {
      this.freeShippingThreshold = threshold * 100; // Convert to cents
      this.updateFreeShippingProgress();
    },

    clear() {
      this.items = [];
      this.count = 0;
      this.total = 0;
      this.freeShippingProgress = 0;
    }
  });

  /**
   * Header Offset Store - Available for custom sticky coordination if needed
   * Note: The default theme uses pure CSS variables for simplicity.
   * This store is available for advanced use cases requiring JS coordination.
   */
  Alpine.store('headerOffset', {
    total: 0,
    setTotal(value) {
      this.total = Math.max(0, Math.floor(value));
      document.documentElement.style.setProperty('--header-top-offset', `${this.total}px`);
    }
  });

  /**
   * UI Store - Centralized UI state (drawers, modals, notifications)
   * Usage in templates: $store.ui.cartOpen, $store.ui.toggleCart()
   */
  Alpine.store('ui', {
    cartOpen: false,
    menuOpen: false,
    searchOpen: false,
    activeModal: null,
    notifications: [],

    toggleCart() {
      this.cartOpen = !this.cartOpen;
      if (this.cartOpen) {
        this.menuOpen = false;
        this.searchOpen = false;
      }
      this.updateBodyScroll();
    },

    openCart() {
      this.cartOpen = true;
      this.menuOpen = false;
      this.searchOpen = false;
      this.updateBodyScroll();
    },

    closeCart() {
      this.cartOpen = false;
      this.updateBodyScroll();
    },

    toggleMenu() {
      this.menuOpen = !this.menuOpen;
      if (this.menuOpen) {
        this.cartOpen = false;
        this.searchOpen = false;
      }
      this.updateBodyScroll();
    },

    openMenu() {
      this.menuOpen = true;
      this.cartOpen = false;
      this.searchOpen = false;
      this.updateBodyScroll();
    },

    closeMenu() {
      this.menuOpen = false;
      this.updateBodyScroll();
    },

    toggleSearch() {
      this.searchOpen = !this.searchOpen;
      if (this.searchOpen) {
        this.cartOpen = false;
        this.menuOpen = false;
      }
    },

    openModal(id) {
      this.activeModal = id;
      this.updateBodyScroll();
    },

    closeModal() {
      this.activeModal = null;
      this.updateBodyScroll();
    },

    closeAll() {
      this.cartOpen = false;
      this.menuOpen = false;
      this.searchOpen = false;
      this.activeModal = null;
      this.updateBodyScroll();
    },

    updateBodyScroll() {
      const hasOpenOverlay = this.cartOpen || this.menuOpen || this.activeModal;
      document.body.style.overflow = hasOpenOverlay ? 'hidden' : '';
    },

    // Toast notifications
    notify(message, type = 'info', duration = 3000) {
      const id = Date.now();
      this.notifications.push({ id, message, type });
      if (duration > 0) {
        setTimeout(() => this.dismissNotification(id), duration);
      }
      return id;
    },

    dismissNotification(id) {
      this.notifications = this.notifications.filter(n => n.id !== id);
    },

    success(message, duration = 3000) {
      return this.notify(message, 'success', duration);
    },

    error(message, duration = 5000) {
      return this.notify(message, 'error', duration);
    }
  });

  // cart:open store sync is handled by cartDrawer.open() to avoid race conditions
  // (cartDrawer defers open until after cart data refresh completes)

  /**
   * Safe Alpine component registration with error boundary
   * Prevents one component failure from breaking others
   */
  const safeRegister = (name, factory) => {
    try {
      Alpine.data(name, factory);
    } catch (error) {
      logger.error(`Failed to register Alpine component '${name}':`, error);
    }
  };

  /**
   * Product Form Component - High-Variant Architecture
   * Optimized for products with many variants (Eurus pattern)
   *
   * Features:
   * - O(1) variant lookup by ID or options key
   * - Pre-computed option availability matrix
   * - Instant media switching per variant
   * - URL history management
   */
  safeRegister('productForm', (config) => ({
    productId: config.productId,
    variantId: config.variantId,
    available: config.available,
    price: config.price,
    compareAtPrice: config.compareAtPrice,
    unitPrice: config.unitPrice || 0,
    quantity: 1,
    selectedOptions: [],
    isAddingToCart: false,
    justAdded: false,
    sendAsGift: false,
    errorMessage: '',
    currentSku: '',
    inventoryQuantity: 0,
    lowStockThreshold: 5,

    // High-variant data structures (populated from VariantData JSON)
    variantData: null,
    variantsById: {},
    variantsByOptions: {},
    availabilityMatrix: {},
    variantMedia: {},

    // AbortController for request deduplication
    _addToCartController: null,

    init() {
      const sectionId = this.$el.closest('[data-section-id]')?.dataset?.sectionId;
      if (!sectionId) {
        logger.error('ProductForm: Section ID not found');
        return;
      }

      // Try enhanced variant data first (high-variant architecture)
      const variantDataEl = document.getElementById(`VariantData-${sectionId}`);
      if (variantDataEl) {
        try {
          const parsedData = JSON.parse(variantDataEl.textContent);

          // Security: Validate JSON structure to prevent prototype pollution
          if (!window.validateJSON(parsedData)) {
            throw new Error('Invalid variant data structure detected');
          }

          this.variantData = parsedData;
          this.variantsById = this.variantData.variantsById || {};
          this.variantsByOptions = this.variantData.variantsByOptions || {};
          this.availabilityMatrix = this.variantData.availabilityMatrix || {};
          this.variantMedia = this.variantData.variantMedia || {};

          // Initialize selected options from current variant
          const currentVariant = this.variantsById[this.variantId];
          if (currentVariant) {
            this.selectedOptions = currentVariant.options || [];
            this.currentSku = currentVariant.sku || '';
            this.inventoryQuantity = currentVariant.inventoryQuantity || 0;
          }

          logger.log('ProductForm: Using high-variant architecture', {
            totalVariants: this.variantData.stats?.totalVariants,
            availableVariants: this.variantData.stats?.availableVariants
          });
        } catch (error) {
          logger.error('ProductForm: Failed to parse variant data', error);
        }
      }

      // Fallback to legacy variant/product JSON if enhanced data not available
      if (!this.variantData) {
        this.initLegacy(sectionId);
      }

      this.updateCurrentVariant();
    },

    // Legacy initialization for backwards compatibility
    initLegacy(sectionId) {
      const variantJson = document.getElementById(`VariantJson-${sectionId}`);
      if (variantJson) {
        try {
          const variants = JSON.parse(variantJson.textContent);

          // Security: Validate array structure
          if (!Array.isArray(variants)) {
            throw new Error('Variants must be an array');
          }

          // Convert to lookup objects for O(1) access
          variants.forEach(v => {
            // Security: Skip invalid variant objects
            if (!v || typeof v !== 'object' || !v.id) return;

            this.variantsById[v.id] = v;
            const key = this.makeOptionsKey(v.option1, v.option2, v.option3);
            this.variantsByOptions[key] = v.id;
            this.availabilityMatrix[key] = v.available;
            if (v.featured_media?.id) {
              this.variantMedia[v.id] = v.featured_media.id;
            }
          });
        } catch (error) {
          logger.error('ProductForm: Failed to parse variant JSON', error);
        }
      }

      const productJson = document.getElementById(`ProductJson-${sectionId}`);
      if (productJson) {
        try {
          const product = JSON.parse(productJson.textContent);

          // Security: Validate product object
          if (!window.validateJSON(product)) {
            throw new Error('Invalid product data structure');
          }

          const currentVariant = this.variantsById[this.variantId];
          if (currentVariant) {
            this.selectedOptions = [
              currentVariant.option1,
              currentVariant.option2,
              currentVariant.option3
            ].filter(Boolean);
          }
        } catch (error) {
          logger.error('ProductForm: Failed to parse product JSON', error);
        }
      }
    },

    // Create lookup key from options (O(1) variant matching)
    makeOptionsKey(...options) {
      return options
        .filter(Boolean)
        .map(o => o.toLowerCase().replace(/[^a-z0-9]+/g, '-'))
        .join('/');
    },

    updateVariant() {
      // Security/QA: Debounce to prevent race conditions from rapid option changes
      if (this._variantUpdateTimeout) {
        clearTimeout(this._variantUpdateTimeout);
      }

      this._variantUpdateTimeout = setTimeout(() => {
        const previousVariantId = this.variantId;
        this.updateCurrentVariant();

        // Only update URL and media if variant actually changed
        if (this.variantId !== previousVariantId) {
          this.updateURL();
          this.updateMedia();
        }
      }, 50); // 50ms debounce - fast enough to feel instant, slow enough to batch rapid changes
    },

    // O(1) variant lookup using pre-computed options key
    updateCurrentVariant() {
      const optionsKey = this.makeOptionsKey(...this.selectedOptions);
      const variantId = this.variantsByOptions[optionsKey];

      if (variantId) {
        const variant = this.variantsById[variantId];
        if (variant) {
          this.variantId = variant.id;
          this.available = variant.available;
          this.price = variant.price;
          this.compareAtPrice = variant.compareAtPrice || variant.compare_at_price || 0;
          this.currentSku = variant.sku || '';
          this.inventoryQuantity = variant.inventoryQuantity || variant.inventory_quantity || 0;

          // Dispatch unified variant:change event for all child components
          // (gallery, inventory, bundle-builder, quantity-breaks)
          const mediaId = this.variantMedia[this.variantId];
          this.$dispatch('variant:change', {
            variant: variant,
            variantId: this.variantId,
            price: this.price,
            compareAtPrice: this.compareAtPrice,
            available: this.available,
            mediaId: mediaId || null
          });
        }
      }
    },

    updateURL() {
      if (!this.variantId) return;
      const url = new URL(window.location);
      url.searchParams.set('variant', this.variantId);
      window.history.replaceState({}, '', url);
    },

    updateMedia() {
      // Media updates are now handled by the unified variant:change event
      // This method is kept for backwards compatibility but does nothing
    },

    // O(1) option availability check using pre-computed matrix
    isOptionAvailable(optionIndex, value) {
      // Build test options array with the new value
      const testOptions = [...this.selectedOptions];
      testOptions[optionIndex] = value;

      const optionsKey = this.makeOptionsKey(...testOptions);

      // Check if this combination exists and is available
      if (optionsKey in this.availabilityMatrix) {
        return this.availabilityMatrix[optionsKey];
      }

      // If exact match not found, check if ANY variant with this option value is available
      // This handles cases where we want to show an option as clickable
      // even if current combination doesn't exist
      const valueKey = value.toLowerCase().replace(/[^a-z0-9]+/g, '-');
      return Object.keys(this.availabilityMatrix).some(key => {
        const parts = key.split('/');
        return parts[optionIndex] === valueKey && this.availabilityMatrix[key];
      });
    },

    // Check if option value exists (regardless of availability)
    doesOptionExist(optionIndex, value) {
      const valueKey = value.toLowerCase().replace(/[^a-z0-9]+/g, '-');
      return Object.keys(this.variantsByOptions).some(key => {
        const parts = key.split('/');
        return parts[optionIndex] === valueKey;
      });
    },

    // Get variant by specific option combination
    getVariantByOptions(options) {
      const key = this.makeOptionsKey(...options);
      const variantId = this.variantsByOptions[key];
      return variantId ? this.variantsById[variantId] : null;
    },

    // Check low stock status
    isLowStock() {
      return this.available &&
             this.inventoryQuantity > 0 &&
             this.inventoryQuantity <= this.lowStockThreshold;
    },

    // Get stock message for display
    getStockMessage() {
      if (!this.available) return 'Sold out';
      if (this.inventoryQuantity <= 0) return 'In stock'; // Oversell allowed
      if (this.isLowStock()) return `Only ${this.inventoryQuantity} left`;
      return 'In stock';
    },

    async addToCart(event) {
      if (this.isAddingToCart) return;

      // QA: Validate form before submission
      this.errorMessage = '';

      // Validate variant is selected
      if (!this.variantId) {
        this.errorMessage = 'Please select all options';
        Alpine.store('ui')?.error(this.errorMessage);
        return;
      }

      // Validate availability
      if (!this.available) {
        this.errorMessage = 'This product is currently out of stock';
        Alpine.store('ui')?.error(this.errorMessage);
        return;
      }

      // Validate quantity
      const qty = parseInt(this.quantity, 10);
      if (isNaN(qty) || qty < 1) {
        this.errorMessage = 'Please select a valid quantity';
        Alpine.store('ui')?.error(this.errorMessage);
        return;
      }

      // Check inventory quantity if tracked (allow oversell if inventory is 0 or negative)
      if (this.inventoryQuantity > 0 && qty > this.inventoryQuantity) {
        this.errorMessage = `Only ${this.inventoryQuantity} available`;
        Alpine.store('ui')?.error(this.errorMessage);
        return;
      }

      // Abort any pending add-to-cart request
      if (this._addToCartController) {
        this._addToCartController.abort();
      }
      this._addToCartController = new AbortController();

      this.isAddingToCart = true;
      this.justAdded = false;

      try {
        await CartAPI.add([{
          id: this.variantId,
          quantity: qty
        }], window.__cartBundledSections || ['cart-icon-bubble'], { signal: this._addToCartController.signal });

        // Show success state on button
        this.isAddingToCart = false;
        this.justAdded = true;
        this._addToCartController = null;

        // Reset success state after 2 seconds
        setTimeout(() => {
          this.justAdded = false;
        }, 2000);

        // Open cart drawer (provides visual feedback, no toast needed)
        document.dispatchEvent(new CustomEvent('cart:open'));

      } catch (error) {
        // Don't show error for aborted requests, but reset state
        if (error.name === 'AbortError') {
          logger.log('Add to cart request aborted');
          this.isAddingToCart = false;
          this._addToCartController = null;
          return;
        }

        logger.error('Add to cart error:', error);
        this.errorMessage = error.message || 'Could not add to cart. Please try again.';
        Alpine.store('ui')?.error(this.errorMessage);
        this.isAddingToCart = false;
        this._addToCartController = null;
      }
    },

    // Share functionality
    async share() {
      const url = window.location.href;
      const title = document.title;

      if (navigator.share) {
        try {
          await navigator.share({ title, url });
        } catch (err) {
          if (err.name !== 'AbortError') {
            this.copyToClipboard(url);
          }
        }
      } else {
        this.copyToClipboard(url);
      }
    },

    copyToClipboard(text) {
      navigator.clipboard.writeText(text).then(() => {
        Alpine.store('ui')?.success('Link copied to clipboard');
      }).catch(() => {
        Alpine.store('ui')?.error('Failed to copy link');
      });
    },

    // Money formatting helper for templates
    formatMoney(cents) {
      return window.formatMoney ? window.formatMoney(cents) : '€' + (cents / 100).toFixed(2).replace('.', ',');
    }
  }));

  /**
   * Product Gallery Component
   */
  safeRegister('productGallery', (config) => ({
    mediaCount: config.mediaCount,
    activeMediaId: null,
    activeIndex: 0,
    lightboxMediaId: null,
    mediaIds: [],

    _touchStartX: 0,
    _touchStartY: 0,

    init() {
      // Collect all media IDs
      this.$el.querySelectorAll('[data-media-id]').forEach((el, index) => {
        const id = parseInt(el.dataset.mediaId);
        this.mediaIds.push(id);
        if (el.classList.contains('is-active') && !this.activeMediaId) {
          this.activeMediaId = id;
          this.activeIndex = index;
        }
      });

      // Listen for variant changes
      this.$el.addEventListener('variant:change', (e) => {
        if (e.detail.mediaId) {
          const index = this.mediaIds.indexOf(e.detail.mediaId);
          if (index !== -1) {
            this.setActiveMedia(e.detail.mediaId, index);
          }
        }
      });

      // Touch swipe support
      const viewer = this.$el.querySelector('.product-gallery__viewer');
      if (viewer) {
        viewer.addEventListener('touchstart', (e) => {
          this._touchStartX = e.touches[0].clientX;
          this._touchStartY = e.touches[0].clientY;
        }, { passive: true });

        viewer.addEventListener('touchend', (e) => {
          const dx = e.changedTouches[0].clientX - this._touchStartX;
          const dy = e.changedTouches[0].clientY - this._touchStartY;
          // Only trigger if horizontal swipe > 50px and more horizontal than vertical
          if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
            if (dx < 0) this.nextMedia();
            else this.prevMedia();
          }
        }, { passive: true });
      }
    },

    setActiveMedia(id, index) {
      this.activeMediaId = id;
      this.activeIndex = index;
    },

    nextMedia() {
      const nextIndex = Math.min(this.activeIndex + 1, this.mediaIds.length - 1);
      this.setActiveMedia(this.mediaIds[nextIndex], nextIndex);
      if (this.lightboxMediaId) this.lightboxMediaId = this.mediaIds[nextIndex];
    },

    prevMedia() {
      const prevIndex = Math.max(this.activeIndex - 1, 0);
      this.setActiveMedia(this.mediaIds[prevIndex], prevIndex);
      if (this.lightboxMediaId) this.lightboxMediaId = this.mediaIds[prevIndex];
    },

    handleZoom(event) {
      const rect = event.currentTarget.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 100;
      const y = ((event.clientY - rect.top) / rect.height) * 100;
      const img = event.currentTarget.querySelector('img');
      if (img) {
        img.style.setProperty('--zoom-x', `${x}%`);
        img.style.setProperty('--zoom-y', `${y}%`);
      }
    },

    resetZoom() {
      this.$el.querySelectorAll('.product-gallery__image').forEach(img => {
        img.style.setProperty('--zoom-x', '50%');
        img.style.setProperty('--zoom-y', '50%');
      });
    },

    openLightbox(mediaId) {
      this.lightboxMediaId = mediaId;
      // Store trigger element for focus return
      this.lightboxTrigger = document.activeElement;
      this.$refs.lightbox?.showModal();
      document.body.style.overflow = 'hidden';
      // Focus the close button for keyboard accessibility
      this.$nextTick(() => {
        const closeBtn = this.$refs.lightbox?.querySelector('.product-lightbox__close');
        if (closeBtn) closeBtn.focus();
      });
    },

    closeLightbox(event) {
      if (event && event.target !== this.$refs.lightbox) return;
      this.$refs.lightbox?.close();
      document.body.style.overflow = '';
      this.lightboxMediaId = null;
      // Return focus to the trigger element
      if (this.lightboxTrigger) {
        this.lightboxTrigger.focus();
        this.lightboxTrigger = null;
      }
    },

    // Focus trap for lightbox accessibility
    trapFocus(event) {
      const lightbox = this.$refs.lightbox;
      if (!lightbox) return;

      const focusableElements = lightbox.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstFocusable = focusableElements[0];
      const lastFocusable = focusableElements[focusableElements.length - 1];

      if (event.shiftKey) {
        // Shift + Tab: wrap to last element
        if (document.activeElement === firstFocusable) {
          event.preventDefault();
          lastFocusable.focus();
        }
      } else {
        // Tab: wrap to first element
        if (document.activeElement === lastFocusable) {
          event.preventDefault();
          firstFocusable.focus();
        }
      }
    }
  }));

  /**
   * Product Media Grid Component
   * Full-width gallery below product description with lightbox.
   */
  safeRegister('productMediaGrid', () => ({
    lightboxOpen: false,
    lightboxIndex: 0,
    images: [],
    triggerEl: null,
    touchStartX: 0,
    touchStartY: 0,
    touchDeltaX: 0,
    touchDeltaY: 0,
    swiping: false,

    init() {
      this.images = [...this.$el.querySelectorAll('[data-lightbox-src]')].map(el => ({
        src: el.dataset.lightboxSrc,
        alt: el.dataset.lightboxAlt,
        thumb: el.dataset.lightboxThumb
      }));
    },

    open(el) {
      this.triggerEl = el;
      const triggers = [...this.$el.querySelectorAll('[data-lightbox-src]')];
      this.lightboxIndex = Math.max(0, triggers.indexOf(el));
      this.lightboxOpen = true;
      this.$nextTick(() => {
        this.$refs.lightboxDialog.showModal();
        this.$refs.closeBtn.focus();
      });
    },

    get currentImage() {
      return this.images[this.lightboxIndex] || { src: '', alt: '', thumb: '' };
    },

    prev() {
      this.lightboxIndex = (this.lightboxIndex - 1 + this.images.length) % this.images.length;
    },

    next() {
      this.lightboxIndex = (this.lightboxIndex + 1) % this.images.length;
    },

    goTo(i) {
      this.lightboxIndex = i;
    },

    closeLightbox() {
      this.lightboxOpen = false;
      this.$refs.lightboxDialog.close();
      if (this.triggerEl) {
        this.triggerEl.focus();
        this.triggerEl = null;
      }
    },

    onTouchStart(e) {
      const t = e.touches[0];
      this.touchStartX = t.clientX;
      this.touchStartY = t.clientY;
      this.touchDeltaX = 0;
      this.touchDeltaY = 0;
      this.swiping = true;
    },

    onTouchMove(e) {
      if (!this.swiping) return;
      const t = e.touches[0];
      this.touchDeltaX = t.clientX - this.touchStartX;
      this.touchDeltaY = t.clientY - this.touchStartY;
    },

    onTouchEnd() {
      if (!this.swiping) return;
      this.swiping = false;
      const absX = Math.abs(this.touchDeltaX);
      const absY = Math.abs(this.touchDeltaY);
      if (absY > 80 && absY > absX * 1.5 && this.touchDeltaY > 0) {
        this.closeLightbox();
      } else if (absX > 40 && absX > absY) {
        this.touchDeltaX < 0 ? this.next() : this.prev();
      }
      this.touchDeltaX = 0;
      this.touchDeltaY = 0;
    },

    trapFocus(e) {
      if (e.key !== 'Tab') return;
      const dialog = this.$refs.lightboxDialog;
      const focusable = dialog.querySelectorAll('button:not([disabled])');
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }));

  /**
   * Product Upsells Component
   */
  safeRegister('productUpsells', () => ({
    products: [],
    bundlePrice: 0,

    async init() {
      const productId = this.$el.dataset.productId;
      const source = this.$el.dataset.upsellSource;

      if (source === 'recommendations') {
        await this.fetchRecommendations(productId);
      }
      // Collection-based upsells are server-rendered
    },

    async fetchRecommendations(productId) {
      try {
        const url = `${window.Shopify?.routes?.root || '/'}recommendations/products.json?product_id=${productId}&limit=3&intent=complementary`;
        const data = await fetchJSON(url);
        this.products = data.products || [];
        this.bundlePrice = this.products.reduce((sum, p) => sum + p.price, 0);
      } catch (error) {
        logger.error('Failed to fetch recommendations:', error);
      }
    },

    async quickAdd(variantId) {
      try {
        await CartAPI.add([{ id: variantId, quantity: 1 }], window.__cartBundledSections || ['cart-icon-bubble']);
        // Show success feedback to user
        if (Alpine.store('ui')?.success) {
          Alpine.store('ui').success('Added to cart');
        }
      } catch (error) {
        logger.error('Quick add failed:', error);
        // Show error feedback to user
        if (Alpine.store('ui')?.error) {
          Alpine.store('ui').error('Failed to add item');
        }
      }
    },

    async addBundle() {
      const items = this.products.map(p => ({
        id: p.variants[0].id,
        quantity: 1
      }));

      try {
        await CartAPI.add(items, window.__cartBundledSections || ['cart-icon-bubble']);
        document.dispatchEvent(new CustomEvent('cart:open'));
      } catch (error) {
        logger.error('Bundle add failed:', error);
      }
    }
  }));

  /**
   * Bundle Builder Component - 2025 Best Practices
   * "Buy X, Save Y%" tiered quantity discounts
   */
  safeRegister('bundleBuilder', (config) => ({
    bundles: config.bundles || [],
    basePrice: config.basePrice || 0,
    variants: config.variants || [],
    options: config.options || [],
    selectedTier: 0,
    bundleItems: [],
    isAdding: false,
    canAddBundle: true,

    init() {
      // Initialize with max possible items
      const maxQty = this.bundles.length > 0 ? Math.max(...this.bundles.map(b => b.qty)) : 0;
      for (let i = 0; i < maxQty; i++) {
        this.bundleItems.push({
          options: this.options.map(opt => opt.values ? opt.values[0] : ''),
          variantId: this.variants[0]?.id
        });
      }

      // Auto-select popular tier if set
      const popularTier = this.bundles.find(b => b.popular);
      if (popularTier) {
        this.selectedTier = popularTier.qty;
      }
    },

    selectTier(qty) {
      this.selectedTier = qty;
    },

    getUnitPrice(tier) {
      return Math.round(this.basePrice * (1 - tier.discount / 100));
    },

    getSavings(tier) {
      const unitSavings = this.basePrice - this.getUnitPrice(tier);
      return unitSavings * tier.qty;
    },

    getTotalSavings() {
      const tier = this.bundles.find(b => b.qty === this.selectedTier);
      return tier ? this.getSavings(tier) : 0;
    },

    getBundleTotal() {
      const tier = this.bundles.find(b => b.qty === this.selectedTier);
      if (!tier) return 0;
      return this.getUnitPrice(tier) * tier.qty;
    },

    updateItemVariant(itemIndex) {
      // Bounds check to prevent index out of range errors
      if (itemIndex < 0 || itemIndex >= this.bundleItems.length) {
        logger.warn('Invalid bundle item index:', itemIndex);
        return;
      }
      const item = this.bundleItems[itemIndex];
      // Find variant matching selected options
      const variant = this.variants.find(v => {
        return item.options.every((opt, i) => v['option' + (i + 1)] === opt);
      });
      if (variant) {
        item.variantId = variant.id;
      }
    },

    formatMoney(cents) {
      return window.formatMoney ? window.formatMoney(cents) : '€' + (cents / 100).toFixed(2).replace('.', ',');
    },

    async addBundleToCart() {
      if (this.isAdding || this.selectedTier === 0) return;

      this.isAdding = true;

      try {
        const items = [];
        const bundleId = Date.now().toString();
        for (let i = 0; i < this.selectedTier; i++) {
          const item = this.bundleItems[i];
          items.push({
            id: item.variantId,
            quantity: 1,
            properties: {
              '_bundle_id': bundleId,
              '_bundle_item': (i + 1) + ' of ' + this.selectedTier
            }
          });
        }

        await CartAPI.add(items, window.__cartBundledSections || ['cart-icon-bubble']);
        document.dispatchEvent(new CustomEvent('cart:open'));

      } catch (error) {
        logger.error('Bundle add error:', error);
        alert('Failed to add bundle to cart. Please try again.');
      } finally {
        this.isAdding = false;
      }
    }
  }));

  /**
   * Product Card Swatches Component - 2025 Best Practices
   * Interactive color swatches for collection pages
   *
   * Features:
   * - Hover: Preview variant image on product card
   * - Click: Navigate to product page with variant preselected
   * - Preload: First 3 variant images for instant switching
   */
  safeRegister('productCardSwatches', (config) => ({
    productId: config.productId,
    productUrl: config.productUrl,
    defaultImageUrl: config.defaultImageUrl,
    swatchData: config.swatchData || [],

    // State
    activeColor: null,
    hoverImageUrl: null,
    hoverColorName: null,
    preloadedImages: new Set(),

    init() {
      // Preload first 3 swatch images for instant switching
      this.preloadImages();
    },

    preloadImages() {
      const toPreload = this.swatchData.slice(0, 3);
      toPreload.forEach(swatch => {
        if (swatch.imageUrl && !this.preloadedImages.has(swatch.imageUrl)) {
          const img = new Image();
          img.src = swatch.imageUrl;
          this.preloadedImages.add(swatch.imageUrl);
        }
      });
    },

    onSwatchHover(colorHandle) {
      const swatch = this.swatchData.find(s => s.handle === colorHandle);
      if (swatch && swatch.imageUrl) {
        this.hoverImageUrl = swatch.imageUrl;
        this.hoverColorName = swatch.value;
        this.activeColor = colorHandle;
      }
    },

    onSwatchLeave() {
      this.hoverImageUrl = null;
      this.hoverColorName = null;
      this.activeColor = null;
    },

    onSwatchClick(colorHandle) {
      const swatch = this.swatchData.find(s => s.handle === colorHandle);
      if (swatch && swatch.variantId) {
        // Navigate to product page with variant preselected
        window.location.href = this.getVariantUrl(swatch.variantId);
      } else {
        // Fallback: navigate to product page without variant
        window.location.href = this.productUrl;
      }
    },

    getVariantUrl(variantId) {
      if (!variantId) return this.productUrl;
      const separator = this.productUrl.includes('?') ? '&' : '?';
      return `${this.productUrl}${separator}variant=${variantId}`;
    },

    getProductUrl() {
      // Used by Alpine for dynamic href binding
      if (this.activeColor) {
        const swatch = this.swatchData.find(s => s.handle === this.activeColor);
        if (swatch && swatch.variantId) {
          return this.getVariantUrl(swatch.variantId);
        }
      }
      return this.productUrl;
    }
  }));

  /**
   * Cart Drawer Component
   */
  safeRegister('cartDrawer', () => ({
    isOpen: false,
    isLoading: false,
    cart: null,
    freeShippingThreshold: 0,
    freeShippingProgress: 0,
    // Cart notes
    cartNote: '',
    noteStatus: '',
    _noteSaveTimeout: null,
    _refreshing: false,
    _openAfterRefresh: false,
    pendingUpdates: null,
    pendingRollbacks: null,

    init() {
      this.freeShippingThreshold = parseFloat(this.$el.dataset.freeShippingThreshold || 0) * 100;

      // Listen for cart events
      // When a refresh is in progress, defer cart:open until after refresh completes
      document.addEventListener('cart:open', () => {
        if (this._refreshing) {
          this._openAfterRefresh = true;
        } else {
          this.open();
        }
      });

      // cart:add carries bundled section HTML — refresh + open
      document.addEventListener('cart:add', (e) => this._refreshThenOpen(e.detail || {}));

      // cart:refresh is dispatched by quick-add, quick-view, upsells (Liquid-based add-to-cart)
      // These callers also dispatch cart:open right after, which we defer until refresh is done
      const handleRefresh = () => this._refreshThenOpen({});
      document.addEventListener('cart:refresh', handleRefresh);
      window.addEventListener('cart:refresh', handleRefresh);

      document.addEventListener('cart:update', (e) => this.onCartUpdate(e.detail));
      document.addEventListener('cart:change', (e) => this.onCartUpdate(e.detail));
      // cart:updated dispatched by gift-wrap-option and image-hotspots
      document.addEventListener('cart:updated', () => this.onCartUpdate({}));
      window.addEventListener('cart:updated', () => this.onCartUpdate({}));
      // cart:clear — refresh drawer to show empty state
      document.addEventListener('cart:clear', () => this.onCartUpdate({}));

      // Expose bundled sections globally so CartAPI callers can include cart-drawer
      window.__cartBundledSections = this.getBundledSections();

      // Initialize Maps for optimistic quantity updates
      this.pendingUpdates = new Map();
      this.pendingRollbacks = new Map();

      // Load initial cart
      this.loadCart();
    },

    async loadCart() {
      try {
        this.cart = await CartAPI.get();
        // Sync global cart store (cart:refresh path doesn't update it)
        this.$store.cart?.updateFromCart(this.cart);
        // Initialize cart note from loaded cart
        if (this.cart && this.cart.note !== undefined) {
          this.cartNote = this.cart.note || '';
        }
        this.updateFreeShippingProgress();
        this.updateCartCount();
      } catch (error) {
        logger.error('Failed to load cart:', error);
      }
    },

    // Auto-save cart note with debounce
    saveCartNote() {
      // Debounce: Clear any pending save
      if (this._noteSaveTimeout) {
        clearTimeout(this._noteSaveTimeout);
      }

      this.noteStatus = 'Saving...';

      this._noteSaveTimeout = setTimeout(async () => {
        try {
          await CartAPI.update({ note: this.cartNote });
          this.noteStatus = 'Saved';
          setTimeout(() => { this.noteStatus = ''; }, 2000);
        } catch (error) {
          logger.error('Failed to save cart note:', error);
          this.noteStatus = 'Error saving';
          setTimeout(() => { this.noteStatus = ''; }, 3000);
        }
      }, 1000); // 1 second debounce
    },

    /**
     * Refresh cart drawer content then open it.
     * Blocks cart:open during refresh so the drawer doesn't flash stale content.
     */
    async _refreshThenOpen(data) {
      this._refreshing = true;
      this._openAfterRefresh = true;
      try {
        await this.onCartUpdate(data);
        // Highlight the first (newest) cart item after add-to-cart
        const firstItem = this.$el.querySelector('.cart-item');
        if (firstItem) {
          firstItem.classList.add('is-new');
          setTimeout(() => firstItem.classList.remove('is-new'), 2500);
        }
      } finally {
        this._refreshing = false;
        if (this._openAfterRefresh) {
          this._openAfterRefresh = false;
          this.open();
        }
      }
    },

    /**
     * Get the cart-drawer section ID from the DOM.
     * Used to request bundled section rendering from Shopify cart API.
     */
    getSectionId() {
      if (!this._sectionId) {
        this._sectionId = this.$el.closest('[id^="shopify-section-"]')?.id?.replace('shopify-section-', '') || 'cart-drawer';
      }
      return this._sectionId;
    },

    /**
     * Returns the section IDs to bundle with every cart API call.
     * Shopify returns fresh HTML for these sections in the API response.
     */
    getBundledSections() {
      return ['cart-icon-bubble', this.getSectionId()];
    },

    async onCartUpdate(data) {
      // Update sections from bundled section rendering (Shopify-recommended approach)
      if (data && data.sections) {
        // Update cart-icon-bubble
        this._updateSection('cart-icon-bubble', data.sections['cart-icon-bubble']);

        // Update cart drawer content directly from bundled response
        this._updateDrawerFromHtml(data.sections[this.getSectionId()]);
      }

      // Refresh cart JSON data (for Alpine reactive state: count, totals, etc.)
      await this.loadCart();

      // If no bundled sections were available, fall back to separate fetch
      if (!data || !data.sections || !data.sections[this.getSectionId()]) {
        await this.refreshDrawerItems();
      }
    },

    /**
     * Replace a section's innerHTML from bundled section HTML.
     */
    _updateSection(sectionId, html) {
      if (typeof html !== 'string') return;
      const section = document.getElementById(`shopify-section-${sectionId}`);
      if (!section) return;
      try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newSection = doc.querySelector(`#shopify-section-${sectionId}`);
        if (newSection) {
          const mutate = window.Alpine?.mutateDom || ((fn) => fn());
          mutate(() => { section.innerHTML = newSection.innerHTML; });
          if (window.Alpine) {
            Array.from(section.children).forEach(child => Alpine.initTree(child));
          }
        }
      } catch (error) {
        logger.error(`Failed to update section ${sectionId}:`, error);
      }
    },

    /**
     * Update cart drawer panel (content + footer) from section HTML.
     * Uses Alpine.mutateDom to properly track DOM changes inside the component.
     */
    _updateDrawerFromHtml(html) {
      if (typeof html !== 'string') return;
      try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const panel = this.$el.querySelector('.cart-drawer__panel');
        if (!panel) return;

        const newContent = doc.querySelector('.cart-drawer__content');
        const currentContent = panel.querySelector('.cart-drawer__content');
        const newFooter = doc.querySelector('.cart-drawer__footer');
        const currentFooter = panel.querySelector('.cart-drawer__footer');

        // Use Alpine.mutateDom so Alpine properly cleans up old and initializes new elements
        const mutate = window.Alpine?.mutateDom || ((fn) => fn());
        mutate(() => {
          // Replace content area (items, upsells, notes, etc.)
          if (newContent && currentContent) {
            currentContent.innerHTML = newContent.innerHTML;
          }

          // Replace footer (subtotal, checkout button) — handles empty↔non-empty transitions
          if (newFooter && currentFooter) {
            currentFooter.innerHTML = newFooter.innerHTML;
          } else if (newFooter && !currentFooter) {
            const footerEl = document.createElement('footer');
            footerEl.className = 'cart-drawer__footer';
            footerEl.innerHTML = newFooter.innerHTML;
            panel.appendChild(footerEl);
          } else if (!newFooter && currentFooter) {
            currentFooter.remove();
          }
        });

        // Initialize Alpine on each new child element (NOT on the container itself)
        if (window.Alpine) {
          if (currentContent) {
            Array.from(currentContent.children).forEach(child => Alpine.initTree(child));
          }
          if (currentFooter && newFooter) {
            Array.from(currentFooter.children).forEach(child => Alpine.initTree(child));
          } else if (!currentFooter && newFooter) {
            const footerEl = panel.querySelector('.cart-drawer__footer');
            if (footerEl) Array.from(footerEl.children).forEach(child => Alpine.initTree(child));
          }
        }
      } catch (error) {
        logger.error('Failed to update drawer from HTML:', error);
      }
    },

    /**
     * Fallback: fetch cart-drawer section HTML separately via Section Rendering API.
     * Only used when bundled sections weren't available in the cart API response.
     */
    async refreshDrawerItems() {
      try {
        const sectionId = this.getSectionId();
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}?sections=${sectionId}`);
        if (!response.ok) return;

        const sections = await response.json();
        this._updateDrawerFromHtml(sections[sectionId]);
      } catch (error) {
        logger.error('Failed to refresh drawer items:', error);
      }
    },

    updateFreeShippingProgress() {
      // Guard against division by zero and invalid values
      if (!this.cart || !this.freeShippingThreshold || this.freeShippingThreshold <= 0) {
        this.freeShippingProgress = 0;
        return;
      }
      this.freeShippingProgress = Math.min(100, (this.cart.total_price / this.freeShippingThreshold) * 100);
    },

    updateCartCount() {
      if (!this.cart) return;
      const previousCount = parseInt(document.querySelector('[data-cart-count]')?.textContent || '0', 10);
      const newCount = this.cart.item_count;

      document.querySelectorAll('[data-cart-count]').forEach(el => {
        el.textContent = newCount;
        el.hidden = newCount === 0;
      });

      // Announce cart changes to screen readers via ARIA live region
      if (newCount !== previousCount) {
        this.announceCartChange(previousCount, newCount);
      }
    },

    announceCartChange(previousCount, newCount) {
      // Find or create ARIA live region
      let liveRegion = document.getElementById('cart-live-region');
      if (!liveRegion) {
        liveRegion = document.createElement('div');
        liveRegion.id = 'cart-live-region';
        liveRegion.setAttribute('aria-live', 'polite');
        liveRegion.setAttribute('aria-atomic', 'true');
        liveRegion.className = 'visually-hidden';
        document.body.appendChild(liveRegion);
        // Verify element was added to DOM
        if (!document.getElementById('cart-live-region')) {
          logger.warn('Failed to create ARIA live region');
          return;
        }
      }

      // Announce the change
      const itemText = newCount === 1 ? 'item' : 'items';
      if (newCount > previousCount) {
        liveRegion.textContent = `Item added to cart. Cart now has ${newCount} ${itemText}.`;
      } else if (newCount < previousCount && newCount > 0) {
        liveRegion.textContent = `Item removed from cart. Cart now has ${newCount} ${itemText}.`;
      } else if (newCount === 0) {
        liveRegion.textContent = 'Cart is now empty.';
      }

      // Clear after announcement (prevents re-reading on focus)
      setTimeout(() => {
        if (liveRegion) liveRegion.textContent = '';
      }, 1000);
    },

    open() {
      this.isOpen = true;
      // Sync with centralized UI store so watchers (e.g. cart-upsells) are notified
      Alpine.store('ui').cartOpen = true;
      // Track open modals to prevent scroll restoration issues
      document.body.dataset.openModals = (parseInt(document.body.dataset.openModals || 0) + 1).toString();
      document.body.style.overflow = 'hidden';
    },

    close() {
      this.isOpen = false;
      Alpine.store('ui').cartOpen = false;
      // Decrement modal count and restore scroll if no modals open
      const count = Math.max(0, parseInt(document.body.dataset.openModals || 0) - 1);
      document.body.dataset.openModals = count.toString();
      if (count === 0) {
        document.body.style.overflow = '';
      }
    },

    handleEscape() {
      if (this.isOpen) this.close();
    },

    // Debounced quantity update to prevent rapid API calls
    updateQuantity(key, quantity) {
      // Security: Validate quantity input
      const validatedQty = parseInt(quantity, 10);
      if (isNaN(validatedQty) || validatedQty < 0) {
        logger.error('Invalid quantity:', quantity);
        Alpine.store('ui')?.error('Invalid quantity');
        return;
      }
      // Enforce Shopify's max quantity limit
      const finalQty = Math.min(Math.max(0, validatedQty), 10000);

      // Clear any pending update for this key

      const existingTimeout = this.pendingUpdates.get(key);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
        // FIX: Rollback previous optimistic update before applying new one
        // This prevents accumulating incorrect price changes from rapid clicks
        const previousRollback = this.pendingRollbacks.get(key);
        if (previousRollback) {
          previousRollback();
          this.pendingRollbacks.delete(key);
        }
      }

      // Find item and store original values for rollback
      const item = this.cart?.items?.find(i => i.key === key);
      if (!item) {
        logger.error('Cart item not found:', key);
        return;
      }

      // Store current (post-rollback) values for new rollback
      const oldQuantity = item.quantity;
      const oldTotalPrice = this.cart.total_price;

      // Optimistic UI update
      item.quantity = finalQty;
      item.isPending = true; // Visual feedback flag
      this.cart.total_price += (finalQty - oldQuantity) * (item.final_price || item.price || 0);
      this.updateFreeShippingProgress();

      // Store rollback function
      const rollback = () => {
        item.quantity = oldQuantity;
        item.isPending = false;
        this.cart.total_price = oldTotalPrice;
        this.updateFreeShippingProgress();
      };
      this.pendingRollbacks.set(key, rollback);

      // Debounce the actual API call
      const timeoutId = setTimeout(async () => {
        this.pendingUpdates.delete(key);
        this.isLoading = true;
        try {
          const result = await CartAPI.change(key, finalQty, window.__cartBundledSections || []);
          // Success: Clear pending state and use server values
          item.isPending = false;
          if (result && result.total_price !== undefined) {
            this.cart.total_price = result.total_price;
            this.cart.item_count = result.item_count;
          }
          this.pendingRollbacks.delete(key);
          this.updateFreeShippingProgress();
          this.updateCartCount();
        } catch (error) {
          // Rollback optimistic update on error
          logger.error('Failed to update quantity:', error);
          const rollbackFn = this.pendingRollbacks.get(key);
          if (rollbackFn) {
            rollbackFn();
            this.pendingRollbacks.delete(key);
          }
          Alpine.store('ui')?.error('Failed to update quantity. Please try again.');
        } finally {
          this.isLoading = false;
        }
      }, 500);

      this.pendingUpdates.set(key, timeoutId);
    },

    async removeItem(key) {
      await this.updateQuantity(key, 0);
    },

    // Get current quantity from reactive cart data
    getQuantity(key) {
      const item = this.cart?.items?.find(i => i.key === key);
      return item?.quantity || 0;
    },

    // Increment quantity (reactive)
    incrementQuantity(key) {
      const currentQty = this.getQuantity(key);
      this.updateQuantity(key, currentQty + 1);
    },

    // Decrement quantity (reactive)
    decrementQuantity(key) {
      const currentQty = this.getQuantity(key);
      if (currentQty > 1) {
        this.updateQuantity(key, currentQty - 1);
      }
    }
  }));

  /**
   * Cart Page Component
   * Full cart page with AJAX quantity updates
   * Used by: sections/main-cart.liquid
   *
   * Note: Uses line index (1-based) directly with Shopify's /cart/change.js
   * because the full cart page has server-rendered content requiring page reload.
   */
  safeRegister('cartPage', () => ({
    isLoading: false,

    async updateQuantity(lineIndex, quantity) {
      if (this.isLoading) return;
      this.isLoading = true;

      try {
        // Use Shopify's native /cart/change.js with line index
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            line: lineIndex,
            quantity: Math.max(0, quantity)
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        // Reload page to reflect changes (cart page has server-rendered content)
        window.location.reload();
      } catch (error) {
        logger.error('Failed to update quantity:', error);
        Alpine.store('ui')?.error('Failed to update cart');
        this.isLoading = false;
      }
    },

    async removeItem(lineIndex) {
      await this.updateQuantity(lineIndex, 0);
    }
  }));

  /**
   * Header Component
   * 2025 features: transparent-to-solid, shrink-on-scroll, hide-on-scroll-down
   */
  safeRegister('siteHeader', () => ({
    isTransparent: false,
    isSticky: true,
    isHidden: false,
    isCompact: false,
    megaMenuOpen: false,
    lastScrollY: 0,
    shrinkThreshold: 100, // Pixels scrolled before shrinking
    transparentThreshold: 50, // Pixels scrolled before transparent becomes solid
    hasTransparentHeader: false,
    // Store references for cleanup
    _scrollHandler: null,
    _transparentObserver: null,

    init() {
      // Check if transparent header is enabled
      this.hasTransparentHeader = this.$el.dataset.transparent === 'true';

      if (this.hasTransparentHeader) {
        // Set initial transparent state based on scroll position
        this.isTransparent = window.scrollY < this.transparentThreshold;
      }

      // Setup scroll behaviors (always needed for transparent header)
      const hasScrollUp = this.$el.dataset.showOnScrollUp === 'true';
      const hasShrink = this.$el.dataset.shrinkOnScroll === 'true';

      // Always setup scroll behavior if transparent header or other scroll features
      if (this.hasTransparentHeader || hasScrollUp || hasShrink) {
        this.setupScrollBehavior(hasScrollUp, hasShrink);
      }
    },

    destroy() {
      // Clean up scroll listener
      if (this._scrollHandler) {
        window.removeEventListener('scroll', this._scrollHandler);
        this._scrollHandler = null;
      }
      // Clean up IntersectionObserver
      if (this._transparentObserver) {
        this._transparentObserver.disconnect();
        this._transparentObserver = null;
      }
    },

    setupScrollBehavior(enableHideOnScrollDown, enableShrink) {
      let ticking = false;

      this._scrollHandler = () => {
        if (!ticking) {
          requestAnimationFrame(() => {
            const currentScrollY = window.scrollY;

            // Transparent to solid on scroll (force solid when mega menu is open)
            if (this.hasTransparentHeader) {
              this.isTransparent = currentScrollY < this.transparentThreshold && !this.megaMenuOpen;
            }

            // Hide on scroll down behavior
            if (enableHideOnScrollDown) {
              const wasHidden = this.isHidden;
              if (currentScrollY > this.lastScrollY && currentScrollY > 100) {
                this.isHidden = true;
              } else {
                this.isHidden = false;
              }
              // Recalculate bars height when header visibility changes
              // (bars may have collapsed/expanded while header was hidden)
              if (wasHidden !== this.isHidden && window.setHeaderHeight) {
                window.setHeaderHeight();
              }
            }

            // Shrink/compact behavior
            if (enableShrink) {
              this.isCompact = currentScrollY > this.shrinkThreshold;
            }

            this.lastScrollY = currentScrollY;
            ticking = false;
          });
          ticking = true;
        }
      };

      window.addEventListener('scroll', this._scrollHandler, { passive: true });
    }
  }));

  /**
   * Predictive Search Component
   */
  safeRegister('predictiveSearch', () => ({
    query: '',
    results: null,
    isLoading: false,
    isOpen: false,

    init() {
      this.search = debounce(this.search.bind(this), 300);
    },

    async search() {
      if (this.query.length < 2) {
        this.results = null;
        return;
      }

      this.isLoading = true;

      try {
        const resources = [];
        if (this.$el.dataset.searchProducts === 'true') resources.push('product');
        if (this.$el.dataset.searchCollections === 'true') resources.push('collection');
        if (this.$el.dataset.searchPages === 'true') resources.push('page');
        if (this.$el.dataset.searchArticles === 'true') resources.push('article');

        const url = `${window.Shopify?.routes?.root || '/'}search/suggest.json?q=${encodeURIComponent(this.query)}&resources[type]=${resources.join(',')}&resources[limit]=6`;

        const data = await fetchJSON(url);
        this.results = data.resources?.results || null;
        this.isOpen = true;
      } catch (error) {
        logger.error('Search failed:', error);
      } finally {
        this.isLoading = false;
      }
    },

    clear() {
      this.query = '';
      this.results = null;
      this.isOpen = false;
    },

    close() {
      this.isOpen = false;
    },

    // Screen reader announcement for results
    getResultsAnnouncement() {
      if (this.isLoading) return 'Searching...';
      if (!this.results) return '';

      const productCount = this.results.products?.length || 0;
      const collectionCount = this.results.collections?.length || 0;
      const pageCount = this.results.pages?.length || 0;
      const total = productCount + collectionCount + pageCount;

      if (total === 0 && this.query.length >= 2) {
        return `No results found for ${this.query}`;
      }

      const parts = [];
      if (productCount > 0) parts.push(`${productCount} product${productCount !== 1 ? 's' : ''}`);
      if (collectionCount > 0) parts.push(`${collectionCount} collection${collectionCount !== 1 ? 's' : ''}`);
      if (pageCount > 0) parts.push(`${pageCount} page${pageCount !== 1 ? 's' : ''}`);

      return parts.length > 0 ? `Found ${parts.join(', ')}` : '';
    }
  }));

  // Search Modal - Full overlay search with predictive results
  safeRegister('searchModal', () => ({
    query: '',
    results: null,
    isLoading: false,
    isOpen: false,
    focusedIndex: -1,
    recentSearches: [],
    previouslyFocused: null,

    init() {
      // Debounced search function
      this.search = debounce(this.performSearch.bind(this), 300);

      // Load recent searches from localStorage
      this.loadRecentSearches();
    },

    open() {
      this.isOpen = true;
      this.previouslyFocused = document.activeElement;
      document.body.classList.add('overflow-hidden');

      // Focus input after transition
      this.$nextTick(() => {
        this.$refs.searchInput?.focus();
      });
    },

    close() {
      this.isOpen = false;
      this.query = '';
      this.results = null;
      this.focusedIndex = -1;
      document.body.classList.remove('overflow-hidden');

      // Restore focus
      if (this.previouslyFocused) {
        this.$nextTick(() => {
          this.previouslyFocused?.focus();
          this.previouslyFocused = null;
        });
      }
    },

    async performSearch() {
      if (this.query.length < 2) {
        this.results = null;
        return;
      }

      this.isLoading = true;
      this.focusedIndex = -1;

      try {
        const resources = [];
        if (this.$el.dataset.searchProducts === 'true') resources.push('product');
        if (this.$el.dataset.searchCollections === 'true') resources.push('collection');
        if (this.$el.dataset.searchPages === 'true') resources.push('page');
        if (this.$el.dataset.searchArticles === 'true') resources.push('article');

        const limit = this.$el.dataset.searchLimit || 8;
        const url = `${window.Shopify?.routes?.root || '/'}search/suggest.json?q=${encodeURIComponent(this.query)}&resources[type]=${resources.join(',')}&resources[limit]=${limit}`;

        const response = await fetch(url);
        const data = await response.json();
        this.results = data.resources?.results || null;
      } catch (error) {
        logger.error('Search failed:', error);
        this.results = null;
      } finally {
        this.isLoading = false;
      }
    },

    clear() {
      this.query = '';
      this.results = null;
      this.focusedIndex = -1;
      this.$refs.searchInput?.focus();
    },

    onSubmit(event) {
      if (this.query.trim()) {
        this.saveRecentSearch(this.query.trim());
      }
    },

    get hasResults() {
      if (!this.results) return false;
      return (
        (this.results.products?.length > 0) ||
        (this.results.collections?.length > 0) ||
        (this.results.pages?.length > 0) ||
        (this.results.articles?.length > 0)
      );
    },

    // Keyboard navigation
    focusNextResult() {
      const products = this.results?.products || [];
      if (products.length === 0) return;

      this.focusedIndex = Math.min(this.focusedIndex + 1, products.length - 1);
      this.focusResultAt(this.focusedIndex);
    },

    focusPrevResult() {
      if (this.focusedIndex <= 0) {
        this.focusedIndex = -1;
        this.$refs.searchInput?.focus();
        return;
      }

      this.focusedIndex = Math.max(this.focusedIndex - 1, 0);
      this.focusResultAt(this.focusedIndex);
    },

    focusResultAt(index) {
      const links = this.$el.querySelectorAll('.search-modal__product-link');
      links[index]?.focus();
    },

    // ACCESSIBILITY: Focus trap for modal
    trapFocus(event) {
      const modal = this.$el;
      if (!modal) return;

      const focusableElements = modal.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );

      if (focusableElements.length === 0) return;

      const firstFocusable = focusableElements[0];
      const lastFocusable = focusableElements[focusableElements.length - 1];

      if (event.shiftKey) {
        // Shift + Tab: wrap to last element
        if (document.activeElement === firstFocusable) {
          event.preventDefault();
          lastFocusable.focus();
        }
      } else {
        // Tab: wrap to first element
        if (document.activeElement === lastFocusable) {
          event.preventDefault();
          firstFocusable.focus();
        }
      }
    },

    // Recent searches (localStorage)
    loadRecentSearches() {
      try {
        const stored = localStorage.getItem('themetonic_recent_searches');
        this.recentSearches = stored ? JSON.parse(stored) : [];
      } catch (error) {
        logger.log('localStorage unavailable or parse error:', error);
        this.recentSearches = [];
      }
    },

    saveRecentSearch(term) {
      try {
        // SECURITY: Sanitize and validate term before storage
        const sanitizedTerm = String(term).trim().substring(0, 100);
        if (!sanitizedTerm || /[<>'"]/g.test(sanitizedTerm)) {
          logger.log('Search term rejected due to invalid characters');
          return;
        }
        // Remove duplicates and add to front
        this.recentSearches = [sanitizedTerm, ...this.recentSearches.filter(t => t !== sanitizedTerm)].slice(0, 5);
        localStorage.setItem('themetonic_recent_searches', JSON.stringify(this.recentSearches));
      } catch (error) {
        logger.log('localStorage save failed:', error);
      }
    },

    // Money formatting helper
    formatMoney(cents) {
      return window.formatMoney ? window.formatMoney(cents) : '€' + (cents / 100).toFixed(2).replace('.', ',');
    }
  }));

  // Mobile Menu - Drill-down navigation with back buttons
  safeRegister('mobileMenu', () => ({
    currentLevel: 0,
    activeSubmenu: null,
    activeSubSubmenu: null,
    currentTitle: '',
    titleHistory: [],
    previouslyFocused: null,

    openSubmenu(title, index) {
      this.titleHistory.push(this.currentTitle);
      this.currentTitle = title;
      this.activeSubmenu = index;
      this.currentLevel = 1;
      this.announceNavigation(title);
    },

    openSubSubmenu(title, index) {
      this.titleHistory.push(this.currentTitle);
      this.currentTitle = title;
      this.activeSubSubmenu = index;
      this.currentLevel = 2;
      this.announceNavigation(title);
    },

    goBack() {
      if (this.currentLevel === 2) {
        this.currentLevel = 1;
        this.activeSubSubmenu = null;
      } else if (this.currentLevel === 1) {
        this.currentLevel = 0;
        this.activeSubmenu = null;
      }
      this.currentTitle = this.titleHistory.pop() || '';
      this.announceNavigation(this.currentTitle || 'Main menu');
    },

    resetMenu() {
      this.currentLevel = 0;
      this.activeSubmenu = null;
      this.activeSubSubmenu = null;
      this.currentTitle = '';
      this.titleHistory = [];
    },

    closeMenu() {
      this.$store.ui.closeMenu();
    },

    // ACCESSIBILITY: Announce navigation changes to screen readers
    announceNavigation(title) {
      const liveRegion = this.$refs.liveRegion;
      if (liveRegion) {
        liveRegion.textContent = `Navigated to ${title}`;
        // Clear after announcement to prevent re-reading
        setTimeout(() => {
          if (liveRegion) liveRegion.textContent = '';
        }, 1000);
      }
    },

    handleMenuStateChange(isOpen) {
      if (isOpen) {
        this.onMenuOpen();
      } else {
        this.onMenuClose();
      }
    },

    onMenuOpen() {
      // Store the previously focused element
      this.previouslyFocused = document.activeElement;

      // Focus the close button after transition
      this.$nextTick(() => {
        const closeBtn = this.$refs.menuPanel?.querySelector('.mobile-menu__close');
        if (closeBtn) {
          closeBtn.focus();
        }
      });
    },

    onMenuClose() {
      // Reset menu state when closing
      this.resetMenu();

      // Restore focus to the previously focused element
      if (this.previouslyFocused && typeof this.previouslyFocused.focus === 'function') {
        this.$nextTick(() => {
          this.previouslyFocused.focus();
          this.previouslyFocused = null;
        });
      }
    },

    // ACCESSIBILITY: Focus trap for keyboard navigation
    handleTabKey(event) {
      const panel = this.$refs.menuPanel;
      if (!panel) return;

      const focusableSelectors = [
        'a[href]',
        'button:not([disabled])',
        'input:not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"])'
      ].join(', ');

      const focusableElements = panel.querySelectorAll(focusableSelectors);
      if (focusableElements.length === 0) return;

      const firstFocusable = focusableElements[0];
      const lastFocusable = focusableElements[focusableElements.length - 1];

      // Trap focus within the panel
      if (event.shiftKey) {
        // Shift + Tab: if on first element, go to last
        if (document.activeElement === firstFocusable) {
          event.preventDefault();
          lastFocusable.focus();
        }
      } else {
        // Tab: if on last element, go to first
        if (document.activeElement === lastFocusable) {
          event.preventDefault();
          firstFocusable.focus();
        }
      }
    }
  }));

  // Mobile Menu Predictive Search - inline search within the mobile drawer
  safeRegister('mobileMenuSearch', () => ({
    query: '',
    results: null,
    isLoading: false,
    showResults: false,
    _abortController: null,

    init() {
      // Debounce the entire search method (same pattern as predictiveSearch)
      this.search = debounce(this.search.bind(this), 300);

      // Listen for menu close to reset state
      this.$watch('$store.ui.menuOpen', (open) => {
        if (!open) this.reset();
      });
    },

    async search() {
      if (this.query.length < 2) {
        this.results = null;
        this.showResults = false;
        this.isLoading = false;
        return;
      }

      this.showResults = true;
      this.isLoading = true;

      // Abort any in-flight request
      if (this._abortController) this._abortController.abort();
      this._abortController = new AbortController();

      const limit = this.$el.dataset.searchLimit || 6;
      try {
        const url = `${window.Shopify?.routes?.root || '/'}search/suggest.json?q=${encodeURIComponent(this.query)}&resources[type]=product,collection&resources[limit]=${limit}`;
        const response = await fetch(url, { signal: this._abortController.signal });
        if (!response.ok) throw new Error(`Search returned ${response.status}`);
        const data = await response.json();
        this.results = data.resources?.results || null;
      } catch (error) {
        if (error.name === 'AbortError') return;
        logger.error('Mobile search failed:', error);
        this.results = null;
      } finally {
        this.isLoading = false;
      }
    },

    clear() {
      if (this._abortController) this._abortController.abort();
      this.query = '';
      this.results = null;
      this.showResults = false;
      this.isLoading = false;
      this.$refs.mobileSearchInput?.focus();
    },

    reset() {
      if (this._abortController) this._abortController.abort();
      this.query = '';
      this.results = null;
      this.showResults = false;
      this.isLoading = false;
    },

    formatPrice(cents) {
      return window.formatMoney ? window.formatMoney(cents) : '\u20AC' + (cents / 100).toFixed(2).replace('.', ',');
    },

    get hasResults() {
      return (this.results?.products?.length > 0) || (this.results?.collections?.length > 0);
    }
  }));

  /**
   * Product Inventory Component
   * Updates stock status reactively when variant changes
   */
  safeRegister('productInventory', (config) => ({
    stockStatus: 'in_stock',
    quantity: 0,
    statusText: '',

    init() {
      this.updateFromVariant(window.productVariants?.[0]);

      // Listen for variant changes (matches productForm dispatch at line 906)
      document.addEventListener('variant:change', (e) => {
        this.updateFromVariant(e.detail.variant);
      });
    },

    updateFromVariant(variant) {
      if (!variant) return;

      this.quantity = variant.inventory_quantity || 0;
      const managed = variant.inventory_management;
      const policy = variant.inventory_policy;

      if (managed && this.quantity <= 0 && policy === 'deny') {
        this.stockStatus = 'out_of_stock';
        this.statusText = config.texts.outOfStock;
      } else if (managed && this.quantity > 0 && this.quantity <= config.threshold) {
        this.stockStatus = 'low_stock';
        if (config.showQuantity) {
          this.statusText = config.texts.lowStock.replace('{quantity}', this.quantity);
        } else {
          this.statusText = config.texts.lowStock.replace(' - {quantity} left', '').replace('{quantity}', '');
        }
      } else {
        this.stockStatus = 'in_stock';
        this.statusText = config.texts.inStock;
      }
    }
  }));

  /**
   * Collection Filters Component
   * AJAX filtering without page reloads for better UX and performance
   *
   * Features:
   * - Intercepts filter form submissions
   * - Updates URL with history.pushState
   * - Fetches and updates product grid via section rendering
   * - Maintains browser back/forward navigation
   * - Debounced filter changes
   * - AbortController for canceling stale requests
   */
  safeRegister('collectionFilters', () => ({
    isLoading: false,
    currentController: null,
    sectionId: '',
    skeletonCount: 8, // Number of skeleton cards to show

    init() {
      // Get section ID from container
      this.sectionId = this.$el.dataset.sectionId || '';

      // Listen for popstate (browser back/forward)
      window.addEventListener('popstate', (event) => {
        if (event.state?.filterUrl) {
          this.filterProducts(event.state.filterUrl, false);
        }
      });

      // Intercept filter form changes
      this.$el.addEventListener('change', (event) => {
        if (event.target.closest('#FacetFiltersForm')) {
          this.handleFilterChange(event);
        }
      });

      // Intercept filter form submissions
      this.$el.addEventListener('submit', (event) => {
        if (event.target.id === 'FacetFiltersForm') {
          event.preventDefault();
          this.submitFilters(event.target);
        }
      });

      // Intercept active filter removal clicks
      this.$el.addEventListener('click', (event) => {
        const filterRemove = event.target.closest('.active-filters__tag, .active-filters__clear');
        if (filterRemove && filterRemove.href) {
          event.preventDefault();
          this.filterProducts(filterRemove.href);
        }
      });

      // Intercept sort dropdown changes
      const sortSelect = this.$el.querySelector('.collection-toolbar__sort');
      if (sortSelect) {
        sortSelect.addEventListener('change', (event) => {
          event.preventDefault();
          this.filterProducts(event.target.value);
        });
        // Prevent default onchange navigation
        sortSelect.removeAttribute('onchange');
      }
    },

    handleFilterChange(event) {
      const form = event.target.closest('#FacetFiltersForm');
      if (!form) return;

      // For checkboxes, submit immediately (debounced)
      if (event.target.type === 'checkbox') {
        this.submitFilters(form);
      }
      // For price range, wait for explicit submit
    },

    submitFilters(form) {
      const formData = new FormData(form);
      const params = new URLSearchParams();

      // Build clean URL params
      for (const [key, value] of formData.entries()) {
        if (value) {
          params.append(key, value);
        }
      }

      // Construct URL
      const baseUrl = window.location.pathname;
      const queryString = params.toString();
      const newUrl = queryString ? `${baseUrl}?${queryString}` : baseUrl;

      this.filterProducts(newUrl);
    },

    async filterProducts(url, pushState = true) {
      // Cancel any pending request
      if (this.currentController) {
        this.currentController.abort();
      }

      this.currentController = new AbortController();
      this.isLoading = true;

      // Update URL in browser
      if (pushState) {
        history.pushState({ filterUrl: url }, '', url);
      }

      // Show skeleton loaders for perceived performance
      this.showSkeletons();

      try {
        // Fetch with section rendering
        const sectionParam = this.sectionId ? `&section_id=${this.sectionId}` : '';
        const fetchUrl = url.includes('?')
          ? `${url}${sectionParam}`
          : `${url}?section_id=${this.sectionId}`;

        const response = await fetch(fetchUrl, {
          signal: this.currentController.signal,
          headers: {
            'Accept': 'text/html'
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const html = await response.text();

        // Parse and update the DOM
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        // Update product grid
        const newGrid = doc.querySelector('#CollectionProductGrid');
        const currentGrid = this.$el.querySelector('#CollectionProductGrid');
        if (newGrid && currentGrid) {
          currentGrid.innerHTML = sanitizeHTML(newGrid.innerHTML);
          // Re-init Alpine on new content
          if (window.Alpine) {
            Alpine.initTree(currentGrid);
          }
        }

        // Update active filters
        const newFilters = doc.querySelector('.active-filters');
        const currentFilters = this.$el.querySelector('.active-filters');
        if (currentFilters) {
          if (newFilters) {
            currentFilters.outerHTML = sanitizeHTML(newFilters.outerHTML);
          } else {
            currentFilters.remove();
          }
        } else if (newFilters) {
          // Insert active filters if they didn't exist before
          const toolbar = this.$el.querySelector('.collection-toolbar');
          if (toolbar) {
            toolbar.insertAdjacentHTML('afterend', sanitizeHTML(newFilters.outerHTML));
          }
        }

        // Update filter counts in sidebar
        const newFacets = doc.querySelector('#FacetFiltersForm');
        const currentFacets = this.$el.querySelector('#FacetFiltersForm');
        if (newFacets && currentFacets) {
          currentFacets.innerHTML = sanitizeHTML(newFacets.innerHTML);
        }

        // Update toolbar count
        const newCount = doc.querySelector('.collection-toolbar__count');
        const currentCount = this.$el.querySelector('.collection-toolbar__count');
        if (newCount && currentCount) {
          currentCount.innerHTML = sanitizeHTML(newCount.innerHTML);
        }

        // Update pagination
        const newPagination = doc.querySelector('.pagination');
        const currentPagination = this.$el.querySelector('.pagination');
        if (currentPagination) {
          if (newPagination) {
            currentPagination.innerHTML = sanitizeHTML(newPagination.innerHTML);
          } else {
            currentPagination.remove();
          }
        } else if (newPagination) {
          const grid = this.$el.querySelector('#CollectionProductGrid');
          if (grid) {
            grid.insertAdjacentHTML('afterend', `<nav class="pagination">${sanitizeHTML(newPagination.innerHTML)}</nav>`);
          }
        }

        // Scroll to top of grid
        const mainSection = this.$el.querySelector('.collection-main');
        if (mainSection) {
          mainSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }

        // Announce for screen readers
        this.announceResults(doc);

      } catch (error) {
        if (error.name === 'AbortError') {
          // Request was cancelled, ignore
          return;
        }
        logger.error('Filter error:', error);
        // Fallback to page navigation on error
        window.location.href = url;
      } finally {
        this.isLoading = false;
        this.currentController = null;
      }
    },

    announceResults(doc) {
      const count = doc.querySelector('.collection-toolbar__count');
      if (count) {
        const liveRegion = document.getElementById('collection-live-region');
        if (liveRegion) {
          liveRegion.textContent = count.textContent.trim();
        }
      }
    },

    showSkeletons() {
      const grid = this.$el.querySelector('#CollectionProductGrid');
      if (!grid) return;

      // Get current grid columns from CSS
      const computedStyle = getComputedStyle(grid);
      const columns = computedStyle.getPropertyValue('--grid-columns') || '4';

      // Generate skeleton HTML
      const skeletonHTML = `
        <div class="product-card product-card--skeleton" aria-busy="true" aria-label="Loading...">
          <div class="product-card__media skeleton skeleton--image"></div>
          <div class="product-card__info">
            <div class="skeleton skeleton--text skeleton--title"></div>
            <div class="skeleton skeleton--text skeleton--price"></div>
            <div class="skeleton skeleton--text skeleton--swatch"></div>
          </div>
        </div>
      `.trim();

      // Replace grid content with skeletons
      const skeletons = Array(this.skeletonCount).fill(0).map(() =>
        `<div class="collection-grid__item">${skeletonHTML}</div>`
      ).join('');

      grid.innerHTML = skeletons;
    }
  }));

  /**
   * Newsletter Popup Component
   * Configurable timed popup with frequency control and AJAX submission
   *
   * Features:
   * - Delayed appearance with localStorage frequency tracking
   * - AJAX form submission with error handling
   * - Focus management for accessibility
   * - ARIA live announcements for screen readers
   */
  safeRegister('newsletterPopup', (config) => ({
    isOpen: false,
    submitted: false,
    isSubmitting: false,
    errorMessage: '',
    delay: config.delay || 4000,
    frequency: config.frequency || 30,
    showOnMobile: config.showOnMobile || false,
    storageKey: 'themetonic_popup',
    previouslyFocused: null,

    init() {
      // Check if should show on mobile
      if (!this.showOnMobile && window.innerWidth < 750) {
        return;
      }

      // Check localStorage for previous dismissal
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        try {
          const data = JSON.parse(stored);
          const daysSinceDismissal = (Date.now() - data.dismissed) / (1000 * 60 * 60 * 24);

          // If frequency is -1, never show again
          if (this.frequency === -1) {
            return;
          }

          // If not enough days have passed, don't show
          if (daysSinceDismissal < this.frequency) {
            return;
          }
        } catch (e) {
          // Invalid localStorage data, continue to show popup
          logger.warn('Invalid popup localStorage data:', e);
        }
      }

      // Show popup after delay
      setTimeout(() => {
        this.open();
      }, this.delay);
    },

    open() {
      // Store currently focused element for focus return
      this.previouslyFocused = document.activeElement;
      this.isOpen = true;
      document.body.style.overflow = 'hidden';

      // Focus management: focus the modal panel after transition
      this.$nextTick(() => {
        const panel = this.$el.querySelector('.newsletter-popup__modal');
        if (panel) {
          // Focus first focusable element or the panel itself
          const firstFocusable = panel.querySelector('input, button, [tabindex]:not([tabindex="-1"])');
          if (firstFocusable) {
            firstFocusable.focus();
          } else {
            panel.setAttribute('tabindex', '-1');
            panel.focus();
          }
        }
      });
    },

    close() {
      this.isOpen = false;
      document.body.style.overflow = '';

      // Store dismissal time
      localStorage.setItem(this.storageKey, JSON.stringify({
        dismissed: Date.now(),
        submitted: this.submitted
      }));

      // Return focus to previously focused element
      if (this.previouslyFocused && typeof this.previouslyFocused.focus === 'function') {
        this.$nextTick(() => {
          this.previouslyFocused.focus();
          this.previouslyFocused = null;
        });
      }
    },

    async handleSubmit(event) {
      event.preventDefault();

      // Prevent double submission
      if (this.isSubmitting) return;

      const form = event.target.closest('form');
      if (!form) return;

      const emailInput = form.querySelector('input[type="email"]');
      const consentCheckbox = form.querySelector('input[name="contact[accepts_marketing]"]');

      // Clear previous error
      this.errorMessage = '';

      // Validate email
      if (!emailInput || !emailInput.validity.valid) {
        emailInput?.reportValidity();
        return;
      }

      // Validate consent if required
      if (consentCheckbox && !consentCheckbox.checked) {
        consentCheckbox.reportValidity();
        return;
      }

      // Submit via AJAX
      this.isSubmitting = true;

      try {
        const formData = new FormData(form);
        const response = await fetch(form.action, {
          method: 'POST',
          body: formData,
          headers: {
            'Accept': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error('Subscription failed. Please try again.');
        }

        // Success
        this.submitted = true;
        this.announceToScreenReader('Successfully subscribed to newsletter');

      } catch (error) {
        logger.error('Newsletter signup error:', error);
        this.errorMessage = error.message || 'An error occurred. Please try again.';
        this.announceToScreenReader(this.errorMessage);
      } finally {
        this.isSubmitting = false;
      }
    },

    // ARIA live region announcement for screen readers
    announceToScreenReader(message) {
      let liveRegion = document.getElementById('newsletter-popup-live-region');
      if (!liveRegion) {
        liveRegion = document.createElement('div');
        liveRegion.id = 'newsletter-popup-live-region';
        liveRegion.setAttribute('aria-live', 'polite');
        liveRegion.setAttribute('aria-atomic', 'true');
        liveRegion.className = 'visually-hidden';
        document.body.appendChild(liveRegion);
      }

      liveRegion.textContent = message;

      // Clear after announcement
      setTimeout(() => {
        if (liveRegion) liveRegion.textContent = '';
      }, 1000);
    }
  }));
});

/* ==========================================================================
   STICKY ADD TO CART
   ========================================================================== */

class StickyAddToCart extends HTMLElement {
  connectedCallback() {
    this.buyButton = document.querySelector('[data-buy-button]');
    if (!this.buyButton) return;

    this.observer = new IntersectionObserver(
      ([entry]) => {
        this.classList.toggle('is-visible', !entry.isIntersecting);
      },
      { threshold: 0, rootMargin: '0px 0px -100px 0px' }
    );

    this.observer.observe(this.buyButton);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }
}

customElements.define('sticky-add-to-cart', StickyAddToCart);

/* ==========================================================================
   QUANTITY INPUT
   ========================================================================== */

class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="number"]');
    this.minusBtn = this.querySelector('[name="minus"]');
    this.plusBtn = this.querySelector('[name="plus"]');

    this.minusBtn?.addEventListener('click', () => this.decrease());
    this.plusBtn?.addEventListener('click', () => this.increase());
  }

  decrease() {
    if (!this.input) return;
    const min = parseInt(this.input.min) || 1;
    const value = parseInt(this.input.value) || 1;
    if (value > min) {
      this.input.value = value - 1;
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  increase() {
    if (!this.input) return;
    const max = parseInt(this.input.max) || Infinity;
    const value = parseInt(this.input.value) || 1;
    if (value < max) {
      this.input.value = value + 1;
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
}

customElements.define('quantity-input', QuantityInput);

/* ==========================================================================
   SCROLL ANIMATIONS (INP-safe with IntersectionObserver)
   ========================================================================== */

/**
 * Attribute-based scroll animations
 * Usage: <div data-animate="slide-up">Content</div>
 * Options: fade, slide-up, slide-down, slide-left, slide-right, scale
 * Modifiers: data-animate-slow, data-animate-fast, data-animate-stagger
 */
(function initScrollAnimations() {
  // Check if scroll animations are disabled via theme settings
  const settings = window.ThemeTonicSettings || {};
  const scrollAnimationsEnabled = settings.enableScrollAnimations !== false;

  // Respect reduced motion preference or disabled setting
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !scrollAnimationsEnabled) {
    // Make all animated elements visible immediately
    document.querySelectorAll('[data-animate]').forEach(el => {
      el.classList.add('is-visible');
    });
    return;
  }

  const animateObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          animateObserver.unobserve(entry.target);
        }
      });
    },
    {
      threshold: 0.1,
      rootMargin: '0px 0px -40px 0px'
    }
  );

  // Observe all elements with data-animate attribute
  const observeAnimatedElements = () => {
    document.querySelectorAll('[data-animate]:not(.is-visible)').forEach(el => {
      animateObserver.observe(el);
    });
  };

  // Initial observation
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeAnimatedElements);
  } else {
    observeAnimatedElements();
  }

  // Re-observe on Shopify section load (for section rendering)
  document.addEventListener('shopify:section:load', observeAnimatedElements);

  // Expose for manual re-initialization
  window.themetonic = window.themetonic || {};
  window.themetonic.observeAnimations = observeAnimatedElements;
})();

/**
 * Legacy scroll-animation Web Component
 * Kept for backwards compatibility
 */
class ScrollAnimation extends HTMLElement {
  connectedCallback() {
    // Check if scroll animations are disabled via theme settings
    const settings = window.ThemeTonicSettings || {};
    const scrollAnimationsEnabled = settings.enableScrollAnimations !== false;

    // Respect reduced motion preference or disabled setting
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !scrollAnimationsEnabled) {
      this.style.opacity = '1';
      this.style.transform = 'none';
      return;
    }

    this.observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          this.classList.add('is-visible');
          this.observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }
}

customElements.define('scroll-animation', ScrollAnimation);

/* ==========================================================================
   PRODUCT FORM (Web Component fallback for non-Alpine)
   ========================================================================== */

class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.submitButton = this.querySelector('[type="submit"]');

    this.form?.addEventListener('submit', this.onSubmit.bind(this));
  }

  async onSubmit(event) {
    event.preventDefault();

    if (this.submitButton.disabled) return;
    this.submitButton.disabled = true;

    const formData = new FormData(this.form);
    const body = {
      items: [{
        id: parseInt(formData.get('id')),
        quantity: parseInt(formData.get('quantity')) || 1
      }]
    };

    try {
      await CartAPI.add(body.items, window.__cartBundledSections || ['cart-icon-bubble']);
      document.dispatchEvent(new CustomEvent('cart:open'));
    } catch (error) {
      logger.error('Form submission error:', error);
    } finally {
      this.submitButton.disabled = false;
    }
  }
}

customElements.define('product-form', ProductForm);

/* ==========================================================================
   NETWORK ERROR HANDLING
   ========================================================================== */

// Detect online/offline status
window.addEventListener('online', () => {
  document.body.classList.remove('is-offline');
  document.dispatchEvent(new CustomEvent('network:online'));
});

window.addEventListener('offline', () => {
  document.body.classList.add('is-offline');
  document.dispatchEvent(new CustomEvent('network:offline'));
});

// Set initial network state
if (!navigator.onLine) {
  document.body.classList.add('is-offline');
}

/* ==========================================================================
   RATE LIMITING
   ========================================================================== */

window.RateLimiter = {
  actions: new Map(),

  check(action, limit = 5, windowMs = 1000) {
    const now = Date.now();
    const timestamps = this.actions.get(action) || [];
    const recent = timestamps.filter(t => now - t < windowMs);

    if (recent.length >= limit) {
      return false; // Rate limited
    }

    recent.push(now);
    this.actions.set(action, recent);
    return true;
  },

  reset(action) {
    this.actions.delete(action);
  }
};

// Apply rate limiting to all Cart API methods
const cartMethods = ['add', 'update', 'change', 'clear'];
cartMethods.forEach(method => {
  const original = window.CartAPI[method].bind(window.CartAPI);
  window.CartAPI[method] = async function(...args) {
    // Rate limits: add=3/sec, update=5/sec, change=5/sec, clear=2/sec
    const limits = { add: 3, update: 5, change: 5, clear: 2 };
    if (!window.RateLimiter.check(`cart:${method}`, limits[method] || 5, 1000)) {
      logger.warn(`Cart ${method} rate limited`);
      throw new Error('Too many requests. Please wait a moment.');
    }
    return original(...args);
  };
});

/* ==========================================================================
   INITIALIZATION
   ========================================================================== */

// Global error handler
window.addEventListener('error', (event) => {
  logger.error('ThemeTonic Error:', event.error);
});

// Unhandled promise rejection handler
window.addEventListener('unhandledrejection', (event) => {
  logger.error('Unhandled Promise Rejection:', event.reason);
});

// Mark JS as loaded
document.documentElement.classList.add('js-loaded');

// Initialize cart store after Alpine is fully ready
document.addEventListener('alpine:initialized', () => {
  if (window.Alpine && Alpine.store('cart')) {
    Alpine.store('cart').init();
  }
});

/* ==========================================================================
   PRODUCT RECOMMENDATIONS LOADER
   Lazy loads recommendations via Shopify API for performance
   ========================================================================== */

(function initProductRecommendations() {
  const initRecommendations = () => {
    const containers = document.querySelectorAll('.product-recommendations[data-url]:not([data-loaded])');
    if (!containers.length) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          loadRecommendations(entry.target);
          observer.unobserve(entry.target);
        }
      });
    }, { rootMargin: '200px' });

    containers.forEach(container => observer.observe(container));
  };

  async function loadRecommendations(el) {
    const url = el.dataset.url;
    if (!url) return;

    el.setAttribute('data-loaded', 'true');

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to load recommendations');

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newContent = doc.querySelector('.product-recommendations');

      if (newContent && newContent.innerHTML.trim()) {
        el.innerHTML = newContent.innerHTML;
      } else {
        // No recommendations available - hide container
        el.style.display = 'none';
      }
    } catch (error) {
      logger.error('Product recommendations error:', error);
      // Hide container on error in production, show message in design mode
      if (window.Shopify?.designMode) {
        el.innerHTML = '<p style="color: #dc2626; padding: 1rem; text-align: center;">Failed to load recommendations.</p>';
      } else {
        el.style.display = 'none';
      }
    }
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initRecommendations);
  } else {
    initRecommendations();
  }

  // Re-initialize on Shopify section load
  document.addEventListener('shopify:section:load', initRecommendations);
})();

// Mega menu: full-width container override (bypasses Liquid cache)
(function() {
  var style = document.createElement('style');
  style.textContent = '.mega-menu__container.page-width { max-width: 100% !important; margin: 0 !important; padding: 32px 48px !important; }';
  document.head.appendChild(style);
})();

// Mega menu: position panel flush below header
(function() {
  var activePanel = null;
  var activeHeader = null;

  function updatePanelPosition() {
    if (activePanel && activeHeader) {
      var headerRect = activeHeader.getBoundingClientRect();

      // backdrop-filter / transform on the header creates a containing block,
      // making position:fixed relative to the header instead of the viewport.
      var cs = getComputedStyle(activeHeader);
      var isContainingBlock = (cs.backdropFilter && cs.backdropFilter !== 'none') ||
                              (cs.webkitBackdropFilter && cs.webkitBackdropFilter !== 'none') ||
                              (cs.transform && cs.transform !== 'none');

      if (isContainingBlock) {
        // Panel is fixed relative to header — use header height
        activePanel.style.top = headerRect.height + 'px';
      } else {
        // Panel is fixed relative to viewport — use header bottom
        activePanel.style.top = headerRect.bottom + 'px';
      }
    }
  }

  document.addEventListener('mouseenter', function(e) {
    var menu = e.target.closest && e.target.closest('.mega-menu');
    if (!menu) return;
    activeHeader = menu.closest('.header');
    activePanel = menu.querySelector('.mega-menu__panel');
    updatePanelPosition();
  }, true);

  document.addEventListener('mouseleave', function(e) {
    var menu = e.target.closest && e.target.closest('.mega-menu');
    if (menu) {
      activePanel = null;
      activeHeader = null;
    }
  }, true);

  window.addEventListener('scroll', updatePanelPosition, { passive: true });
})();

// Mega menu: dynamic hover handled by Alpine @mouseover in mega-nav.liquid
